<template>
    <v-container class="py-5 px-5" fluid v-if="this.$store.state.signedIn">
        <v-layout justify-center wrap>
            <v-flex xs12 md10 class="py-2 px-2">
                <v-toolbar dark flat color="secondary">
                    <v-toolbar-title @click="showOrgASNs=!showOrgASNs" :style="$store.state.textFormatting.headers"> {{organisation}} ASNs</v-toolbar-title>
                    <v-progress-circular v-if="loading" indeterminate color="white"></v-progress-circular>
                    <v-spacer></v-spacer>
                    <v-flex shrink align-center justify-center>
                        <br />
                        <v-switch v-model="showAdvanced" @change="advancedChange()">
                            <template v-slot:label>
                                <div :style="$store.state.textFormatting.main"><b>Advanced settings</b></div>
                            </template>
                        </v-switch>
                    </v-flex>
                    <downloadcsv
                        class   = "btn btn-default"
                        :data   = "organisation_download_asns"
                        name = "orgASNs.csv">
                        <v-tooltip top eager color="primary" id="exit-app">
                            <template v-slot:activator="{ on }">
                                <v-btn small dark v-on="on" color="success" :style="$store.state.textFormatting.main"> <v-icon>save_alt</v-icon> Org ASNs</v-btn>
                            </template>
                            <span :style="$store.state.textFormatting.main">CSV of organisation ASNs</span>
                        </v-tooltip>        
                    </downloadcsv>
                    <div v-if="user_type!='Analyst'">
                        <v-btn @click="clickASN('Click here to add ASN', '', 0, 0, 0, 'N', 1, 'organisation', '', '', '')" color="success" :style="$store.state.textFormatting.main"> Add ASN <v-icon>add</v-icon> </v-btn>
                    </div>
                    <div v-if="user_type=='Analyst'">
                        <v-btn disabled color="success" :style="$store.state.textFormatting.main"> Add ASN <v-icon>add</v-icon> </v-btn>
                    </div>
                    
                    <info>
                        <template slot>
                            Add an ASN to your organisation, or remove/ edit existing ASNs.
                        </template>
                        <template slot="body">
                            The alerting mechanism works in the following way: <br />
                            
                            <p> <h2> SIMPLE ALERTING (no advanced settings) </h2> </p>
                            <ul>
                                <li> A user can define an ASN, give it a name (for housekeeping), and will be alerted upon <b>ownership assertions</b> involving the ASN (where this ASN has either asserted ownership over a prefix or had its ownership taken by another ASN). </li>
                                <li> You will <b>not receive emails</b> and alerts are created <b>regardless of hijack and impact scores</b> created by the system. </li> 
                            </ul>

                            <p> <h2> ADVANCED ALERTING (advanced settings) </h2> </p>
                            <ul>
                                <li> User can specify <b>which event types to be alerted upon</b>, of which there are 4 in total: 
                                    <ul>
                                        <li><b>Ownership Assertions</b></li>
                                        <li><b>Path Changes</b></li>
                                        <li><b>First Seens</b></li>
                                        <li><b>AS in path alerts</b> (NOTE: These can appear as any of the above types in the organisation alerts page, since they depend only on the AS being in the path somewhere in an event)</li>
                                    </ul>
                                    There are descriptions of each of these in <b>hover over panels throughout the page - hover your cursor where you see any of these terms</b>
                                </li>
                                <li> The user can also decide whether to receive emails from these alerts, again split out by the event type </li>
                                <li> Hijack and Impact score thresholds can also be set, which can further refine down the amount of alerts you will receive. </li>
                                <li>
                                    The scores of the <b> event </b> are compared with the <b> thresholds </b>. If hijack <b> and/or (set by the user for each ASN - default AND) </b>
                                    impact scores are <b> greater than or equal to </b> the given thresholds, a notification/ alert is created in the mySQL table. There are some predefined thresholds:
                                    <ul>
                                        <li> <b> Regular: H = 30, I = 10, V = 5 </b> for when you are interested in some potentially dangerous anomalies, but not the less dangerous ones </li>
                                        <li> <b> High Importance: H = 20, I = 8, V = 2 </b> for when you want to know about most anomalies, but not those we deem very unlikely to be hijack events </li>
                                        <li> <b> Low Importance: H = 50, I = 25, V = 10 </b> for when you only need to see the very high risk anomalies </li>
                                        <li> <b> Show All: H = 0, I = 0, V = 0 (default)</b> will trigger alerts for all incoming events </li>
                                    </ul>
                                </li>
                                
                                <li> When an alert occurs with emails turned on, the following occurs:
                                    <ul>
                                        <li> An initial email is sent out, alerting the user of the alert beginning </li>
                                        <li> When additional events occur with the same basic data (in the same 5 min window), and the total 
                                            passes the <b>Repeat Email Alert Threshold - set by the user</b>, an additional email is sent out </li>
                                        <li> This continues until the 5 minute window elapses, at which point a new notification / alert will 
                                            begin </li>
                                    </ul>
                                </li>
                                <li> <b>Lastly</b>, the user may add multiple ASNs to an <b>AS Group</b>. ASNs in a common group behave in one of the following, user defined, ways:
                                    <ul>
                                        <li><b>EXCLUDE:</b> Ownership assertions between ASNs in the group will <b>not create alerts</b> e.g. if I have AS 1 and AS 2 in a group and AS 2 asserts ownership over one of AS 1's prefixes, I will not be alerted </li>
                                        <li><b>HIJACK:</b> Ownership assertions between ASNs in the group will have their hijack scores overriden and set to the user defined score </li>
                                    </ul>
                                </li>
                            </ul>
                            <br />
                            
                            <b> NOTE: Updates to thresholds work retrospectively, so will not alter previously created alerts. There is also a delay in the analytics engine picking up the changes, so you may continue to receive alerts after making changes. </b>
                        </template>
                    </info>
                </v-toolbar>  
                <v-card v-if="showOrgASNs">  
                    <v-data-table :headers="asnHeaders" :items="orgASNs" :key="id"
                            :rows-per-page-items="rowsPerPageItems" :pagination.sync="pagination" v-if="!loading"> 
                            <template slot="headerCell" slot-scope="props">
                                <v-tooltip top eager>
                                <span slot="activator" :style="$store.state.textFormatting.main">
                                    {{ props.header.text }}
                                </span>
                                <span :style="$store.state.textFormatting.main">
                                    {{ props.header.ad_text }}
                                </span>
                                </v-tooltip>
                            </template>   
                            <template slot="items" slot-scope="props">
                                <tr>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main">
                                        <v-tooltip top eager color="primary" id="exit-app" v-if="is_asn_owners">
                                            <template v-slot:activator="{ on }">
                                                <div v-on="on" :style="$store.state.textFormatting.main"> <b> {{ props.item.asn }} </b></div>
                                            </template>
                                            <span :style="$store.state.textFormatting.main"> {{ asn_own_dict[props.item.asn] }}</span>
                                        </v-tooltip>
                                        <div v-if="!is_asn_owners" :style="$store.state.textFormatting.main"> <b> {{ props.item.asn }} </b> <v-progress-circular size="12" indeterminate color="purple" /></div>
                                    </td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.asn_name }}</td>
                                    <td :style="$store.state.textFormatting.main" class="text-xs-center" v-if="showAdvanced"> {{ props.item.as_group }} </td>
                                    <td :style="$store.state.textFormatting.main" class="text-xs-center" v-if="showAdvanced"> 
                                        <v-icon v-if="props.item.opf_split[0]"> check </v-icon>
                                        <v-icon v-if="!props.item.opf_split[0]"> clear </v-icon>
                                        <span v-if="props.item.opf_split[0]">
                                            (
                                            <v-icon v-if="props.item.opf_split[4]"> check </v-icon>
                                            <v-icon v-if="!props.item.opf_split[4]"> clear </v-icon>
                                            <v-icon v-if="props.item.opf_split_e[0]"> check </v-icon>
                                            <v-icon v-if="!props.item.opf_split_e[0]"> clear </v-icon>
                                            )
                                        </span>
                                    </td>
                                    <td :style="$store.state.textFormatting.main" class="text-xs-center" v-if="showAdvanced"> 
                                        <v-icon v-if="props.item.opf_split[1]"> check </v-icon>
                                        <v-icon v-if="!props.item.opf_split[1]"> clear </v-icon>
                                        <span v-if="props.item.opf_split[1]">
                                            (
                                            <v-icon v-if="props.item.opf_split_e[1]"> check </v-icon>
                                            <v-icon v-if="!props.item.opf_split_e[1]"> clear </v-icon>
                                            )
                                        </span>
                                    </td>
                                    <td :style="$store.state.textFormatting.main" class="text-xs-center" v-if="showAdvanced"> 
                                        <v-icon v-if="props.item.opf_split[2]"> check </v-icon>
                                        <v-icon v-if="!props.item.opf_split[2]"> clear </v-icon>
                                        <span v-if="props.item.opf_split[2]">
                                            (
                                            <v-icon v-if="props.item.opf_split_e[2]"> check </v-icon>
                                            <v-icon v-if="!props.item.opf_split_e[2]"> clear </v-icon>
                                            )
                                        </span>
                                    </td>
                                    <td :style="$store.state.textFormatting.main" class="text-xs-center" v-if="showAdvanced"> 
                                        <v-icon v-if="props.item.opf_split[3]"> check </v-icon>
                                        <v-icon v-if="!props.item.opf_split[3]"> clear </v-icon>
                                        <span v-if="props.item.opf_split[3]">
                                            (
                                            <v-icon v-if="props.item.opf_split_e[3]"> check </v-icon>
                                            <v-icon v-if="!props.item.opf_split_e[3]"> clear </v-icon>
                                            )
                                        </span>
                                    </td>
                                    


                                    <td class="text-xs-center" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.H }}</td>
                                    
                                    
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.and_or }}</td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.I }}</td>

                                    <td class="text-xs-center" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.V }}</td>
                                    <!-- <td class="text-xs-center"> {{ props.item.peering_asn }}</td> !-->
                                    <!--<td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.alerting_type }}</td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.exclude_peer }}</td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.emails }}</td>-->
                                    
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.notification_count }}</td>
                                    
                                    <td class="text-xs-center">
                                        <v-tooltip v-if="user_type!='Analyst'" top eager close-delay=100>
                                            <template  v-slot:activator="{ on }">
                                                <v-icon v-on="on" small class="mr-2" @click="clickASN(props.item.asn, props.item.asn_name,
                                                props.item.H, props.item.I, props.item.V,
                                                props.item.peering_asn, props.item.type, 'organisation', props.item.alerting_type, 
                                                props.item.exclude_peer, props.item.emails, props.item.and_or, props.item.as_group,
                                                props.item.opf_split, props.item.opf_split_e, props.item.own_path_first, props.item.emails_opf)" :style="$store.state.textFormatting.main"> edit </v-icon>
                                            </template>
                                            <span :style="$store.state.textFormatting.main">Edit ASN</span>
                                        </v-tooltip>
                                        
                                        <v-tooltip  v-if="user_type!='Analyst'" top eager close-delay=100>
                                            <template  v-slot:activator="{ on }">
                                                <v-icon v-on="on" small class="mr-2" @click="prepRemoveASN(props.item, 'organisation')" :style="$store.state.textFormatting.main"> delete </v-icon>
                                            </template>
                                            <span :style="$store.state.textFormatting.main">Remove ASN</span>
                                        </v-tooltip>                                        
                                    </td>
                                </tr>
                                
                            </template>
                        <template slot="no-data">
                            <v-alert :value="true" color="success" icon="warning" :style="$store.state.textFormatting.main">
                            No organisation ASNs, click 'ADD ASN +' to add one
                            </v-alert>
                        </template>
                    </v-data-table>
                </v-card>
            </v-flex>
            <v-flex xs12 md10 class="py-2 px-2">
                <v-toolbar dark flat color="secondary">
                    <v-toolbar-title @click="showUserASNs=!showUserASNs" :style="$store.state.textFormatting.headers"> {{username}}'s ASNs</v-toolbar-title>
                    <v-progress-circular v-if="loading2" indeterminate color="white"></v-progress-circular>
                    <v-spacer></v-spacer>
                    <v-flex shrink align-center justify-center>
                        <br />
                        <v-switch v-model="showAdvanced" @change="advancedChange()">
                            <template v-slot:label>
                                <div :style="$store.state.textFormatting.main"><b>Advanced settings</b></div>
                            </template>
                        </v-switch>
                    </v-flex>
                    <downloadcsv
                        class   = "btn btn-default"
                        :data   = "user_download_asns"
                        name = "userASNs.csv">
                        <v-tooltip top eager color="primary" id="exit-app">
                            <template v-slot:activator="{ on }">
                                <v-btn small dark v-on="on" color="success" :style="$store.state.textFormatting.main"> <v-icon>save_alt</v-icon> User ASNs </v-btn>
                            </template>
                            <span :style="$store.state.textFormatting.main">CSV of user ASNs</span>
                        </v-tooltip>        
                    </downloadcsv>
                    <v-btn @click="clickASN('Click here to add ASN', '', 0, 0, 0, 'N', 1, 'user')" color="success" :style="$store.state.textFormatting.main"> Add ASN <v-icon>add</v-icon> </v-btn>
                    
                    <info>
                        <template slot>
                            Add an ASN to your specific user, or remove/ edit existing ASNs.
                        </template>
                         <template slot="body">
                            The alerting mechanism works in the following way: <br />
                            
                            <p> <h2> SIMPLE ALERTING (no advanced settings) </h2> </p>
                            <ul>
                                <li> A user can define an ASN, give it a name (for housekeeping), and will be alerted upon <b>ownership assertions</b> involving the ASN (where this ASN has either asserted ownership over a prefix or had its ownership taken by another ASN). </li>
                                <li> You will <b>not receive emails</b> and alerts are created <b>regardless of hijack and impact scores</b> created by the system. </li> 
                            </ul>

                            <p> <h2> ADVANCED ALERTING (advanced settings) </h2> </p>
                            <ul>
                                <li> User can specify <b>which event types to be alerted upon</b>, of which there are 4 in total: 
                                    <ul>
                                        <li><b>Ownership Assertions</b></li>
                                        <li><b>Path Changes</b></li>
                                        <li><b>First Seens</b></li>
                                        <li><b>AS in path alerts</b> (NOTE: These can appear as any of the above types in the user alerts page, since they depend only on the AS being in the path somewhere in an event)</li>
                                    </ul>
                                    There are descriptions of each of these in <b>hover over panels throughout the page - hover your cursor where you see any of these terms</b>
                                </li>
                                <li> The user can also decide whether to receive emails from these alerts, again split out by the event type </li>
                                <li> Hijack and Impact score thresholds can also be set, which can further refine down the amount of alerts you will receive. </li>
                                <li>
                                    The scores of the <b> event </b> are compared with the <b> thresholds </b>. If hijack <b> and/or (set by the user for each ASN - default AND) </b>
                                    impact scores are <b> greater than or equal to </b> the given thresholds, a notification/ alert is created in the mySQL table. There are some predefined thresholds:
                                    <ul>
                                        <li> <b> Regular: H = 30, I = 10, V = 5 </b> for when you are interested in some potentially dangerous anomalies, but not the less dangerous ones </li>
                                        <li> <b> High Importance: H = 20, I = 8, V = 2 </b> for when you want to know about most anomalies, but not those we deem very unlikely to be hijack events </li>
                                        <li> <b> Low Importance: H = 50, I = 25, V = 10 </b> for when you only need to see the very high risk anomalies </li>
                                        <li> <b> Show All: H = 0, I = 0, V = 0 (default)</b> will trigger alerts for all incoming events </li>
                                    </ul>
                                </li>
                                
                                <li> When an alert occurs with emails turned on, the following occurs:
                                    <ul>
                                        <li> An initial email is sent out, alerting the user of the alert beginning </li>
                                        <li> When additional events occur with the same basic data (in the same 5 min window), and the total 
                                            passes the <b>Repeat Email Alert Threshold - set by the user</b>, an additional email is sent out </li>
                                        <li> This continues until the 5 minute window elapses, at which point a new notification / alert will 
                                            begin </li>
                                    </ul>
                                </li>
                                <li> <b>Lastly</b>, the user may add multiple ASNs to an <b>AS Group</b>. ASNs in a common group behave in one of the following, user defined, ways:
                                    <ul>
                                        <li><b>EXCLUDE:</b> Ownership assertions between ASNs in the group will <b>not create alerts</b> e.g. if I have AS 1 and AS 2 in a group and AS 2 asserts ownership over one of AS 1's prefixes, I will not be alerted </li>
                                        <li><b>HIJACK:</b> Ownership assertions between ASNs in the group will have their hijack scores overriden and set to the user defined score </li>
                                    </ul>
                                </li>
                            </ul>
                            <br />
                            
                            <b> NOTE: Updates to thresholds work retrospectively, so will not alter previously created alerts. There is also a delay in the analytics engine picking up the changes, so you may continue to receive alerts after making changes. </b>
                        </template>
                    </info>
                </v-toolbar>  
                <v-card v-if="showUserASNs">  
                    <v-data-table :headers="asnHeaders" :items="userASNs" :key="id"
                            :rows-per-page-items="rowsPerPageItems" :pagination.sync="pagination" v-if="!loading2"
                            >
                            <template slot="headerCell" slot-scope="props">
                                <v-tooltip top eager>
                                <span slot="activator" :style="$store.state.textFormatting.main">
                                    {{ props.header.text }}
                                </span>
                                <span :style="$store.state.textFormatting.main">
                                    {{ props.header.ad_text }}
                                </span>
                                </v-tooltip>
                            </template>
                            <template slot="items" slot-scope="props">
                                <tr>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main">
                                        <v-tooltip top eager color="primary" id="exit-app" v-if="is_asn_owners">
                                            <template v-slot:activator="{ on }">
                                                <div v-on="on" :style="$store.state.textFormatting.main"> <b> {{ props.item.asn }} </b></div>
                                            </template>
                                            <span :style="$store.state.textFormatting.main"> {{ asn_own_dict[props.item.asn] }}</span>
                                        </v-tooltip>
                                        <div v-if="!is_asn_owners" :style="$store.state.textFormatting.main"> <b> {{ props.item.asn }} </b> <v-progress-circular size="12" indeterminate color="purple" /></div>
                                    </td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.asn_name }} </td>
                                    <td :style="$store.state.textFormatting.main" class="text-xs-center" v-if="showAdvanced"> {{ props.item.as_group }} </td>
                                    <td :style="$store.state.textFormatting.main" class="text-xs-center" v-if="showAdvanced"> 
                                        <v-icon v-if="props.item.opf_split[0]"> check </v-icon>
                                        <v-icon v-if="!props.item.opf_split[0]"> clear </v-icon>
                                        <span v-if="props.item.opf_split[0]">
                                            (
                                            <v-icon v-if="props.item.opf_split[4]"> check </v-icon>
                                            <v-icon v-if="!props.item.opf_split[4]"> clear </v-icon>
                                            <v-icon v-if="props.item.opf_split_e[0]"> check </v-icon>
                                            <v-icon v-if="!props.item.opf_split_e[0]"> clear </v-icon>
                                            )
                                        </span>
                                    </td>
                                    <td :style="$store.state.textFormatting.main" class="text-xs-center" v-if="showAdvanced"> 
                                        <v-icon v-if="props.item.opf_split[1]"> check </v-icon>
                                        <v-icon v-if="!props.item.opf_split[1]"> clear </v-icon>
                                        <span v-if="props.item.opf_split[1]">
                                            (
                                            <v-icon v-if="props.item.opf_split_e[1]"> check </v-icon>
                                            <v-icon v-if="!props.item.opf_split_e[1]"> clear </v-icon>
                                            )
                                        </span>
                                    </td>
                                    <td :style="$store.state.textFormatting.main" class="text-xs-center" v-if="showAdvanced"> 
                                        <v-icon v-if="props.item.opf_split[2]"> check </v-icon>
                                        <v-icon v-if="!props.item.opf_split[2]"> clear </v-icon>
                                        <span v-if="props.item.opf_split[2]">
                                            (
                                            <v-icon v-if="props.item.opf_split_e[2]"> check </v-icon>
                                            <v-icon v-if="!props.item.opf_split_e[2]"> clear </v-icon>
                                            )
                                        </span>
                                    </td>
                                    <td :style="$store.state.textFormatting.main" class="text-xs-center" v-if="showAdvanced"> 
                                        <v-icon v-if="props.item.opf_split[3]"> check </v-icon>
                                        <v-icon v-if="!props.item.opf_split[3]"> clear </v-icon>
                                        <span v-if="props.item.opf_split[3]">
                                            (
                                            <v-icon v-if="props.item.opf_split_e[3]"> check </v-icon>
                                            <v-icon v-if="!props.item.opf_split_e[3]"> clear </v-icon>
                                            )
                                        </span>
                                    </td>
                                    


                                    <td class="text-xs-center" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.H }}</td>
                                    
                                    
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.and_or }}</td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.I }}</td>

                                    <td class="text-xs-center" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.V }}</td>
                                    <!-- <td class="text-xs-center"> {{ props.item.peering_asn }}</td> !-->
                                    <!--<td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.alerting_type }}</td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.exclude_peer }}</td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.emails }}</td>-->
                                    
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.notification_count }}</td>
                                    
                                    <td class="text-xs-center">
                                        <v-tooltip top eager close-delay=100>
                                            <template  v-slot:activator="{ on }">
                                                <v-icon v-on="on" small class="mr-2" @click="clickASN(props.item.asn, props.item.asn_name,
                                                props.item.H, props.item.I, props.item.V,
                                                props.item.peering_asn, props.item.type, 'user', props.item.alerting_type, 
                                                props.item.exclude_peer, props.item.emails, props.item.and_or, props.item.as_group,
                                                props.item.opf_split, props.item.opf_split_e, props.item.own_path_first, props.item.emails_opf)" :style="$store.state.textFormatting.main"> edit </v-icon>
                                            </template>
                                            <span :style="$store.state.textFormatting.main">Edit ASN</span>
                                        </v-tooltip>
                                        
                                        <v-tooltip top eager close-delay=100>
                                            <template  v-slot:activator="{ on }">
                                                <v-icon v-on="on" small class="mr-2" @click="prepRemoveASN(props.item, 'user')" :style="$store.state.textFormatting.main"> delete </v-icon>
                                            </template>
                                            <span :style="$store.state.textFormatting.main">Remove ASN</span>
                                        </v-tooltip>      
                                    </td>
                                </tr>
                                
                            </template>
                        <template slot="no-data">
                            <v-alert :value="true" color="success" icon="warning" :style="$store.state.textFormatting.main">
                            No user ASNs, click 'ADD ASN +' to add one
                            </v-alert>
                        </template>
                    </v-data-table>
                    <v-layout align-right justify-center v-if="userASNs.length > 1">
                        <v-spacer />
                        <v-flex shrink class="py-2 px-2">
                            <v-btn @click="showPurge=true" color="red">Delete All ASNs</v-btn>
                        </v-flex>
                    </v-layout>
                </v-card>
            </v-flex>
            <v-flex xs12 xl10 v-if="showEdit" class="px-2 py-2">
                <v-toolbar flat dark color="secondary">
                    <v-toolbar-title v-if="edType == 'Add'" :style="$store.state.textFormatting.headers">Add ASN to your {{ addType }} </v-toolbar-title>
                    <v-toolbar-title v-if="edType == 'Edit'" :style="$store.state.textFormatting.headers">Edit {{ addType }} AS {{ form.asn }} </v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn color="#3F8993" class="mr-4" @click="csvOpen" v-if="edType == 'Add'" :style="$store.state.textFormatting.main"> CSV ASN Add </v-btn>
                    <v-btn color="success" class="mr-4" @click="addASN" v-if="edType == 'Add'" :disabled="!valid" :style="$store.state.textFormatting.main"> Submit </v-btn>
                    <v-btn color="success" class="mr-4" @click="editASN" v-if="edType == 'Edit'" :disabled="!valid" :style="$store.state.textFormatting.main"> Submit </v-btn>
                    <!-- <v-btn color="red" class="mr-4" @click="prepRemoveASN" v-if="edType == 'Edit'"> Remove ASN </v-btn> !-->
                    <v-btn class="mx-2" fab small color="blue" @click="shrinkEditWindow">
                        <h3 :style="$store.state.textFormatting.main">-</h3>
                    </v-btn>
                </v-toolbar>
                <v-card>
                    <v-container fluid>
                        <v-card-text>
                            <v-form ref="form" v-model="valid" lazy-validation>
                                
                                    <v-layout wrap justify-center row>
                                        <v-flex xs6 class="px-2">
                                            <v-text-field :style="$store.state.textFormatting.main" v-model="form.asn_name" label="ASN Name"></v-text-field>
                                        </v-flex>
                                        <v-flex xs6 class="px-2" v-if="edType == 'Add'">
                                            <v-text-field :style="$store.state.textFormatting.main" v-model="form.asn" :rules="asn_rules" required label="ASN"></v-text-field>
                                        </v-flex>
                                    </v-layout>
                                    <v-layout>
                                        <v-flex shrink align-center justify-center>
                                            <v-switch v-model="showAdvanced" @change="advancedChange()">
                                                <template v-slot:label>
                                                    <div :style="$store.state.textFormatting.main">Advanced settings</div>
                                                </template>
                                            </v-switch>
                                        </v-flex>
                                    </v-layout>
                                    <v-card>
                                        <v-layout v-if="showAdvanced">

                                            <v-flex align-center justify-center>
                                                <v-layout>
                                                    <v-tooltip top eager close-delay=500 >
                                                        <template  v-slot:activator="{ on }">
                                                            <div slot="activator" v-on="on" :style="$store.state.textFormatting.main"><b>Ownership Assertions?</b></div>
                                                        </template>
                                                        <span :style="$store.state.textFormatting.main">Alerts where the origin (ownership) ASN has changed.</span>
                                                    </v-tooltip>
                                                </v-layout>
                                                <v-layout>
                                                    <v-flex shrink class="px-2">
                                                        <v-switch v-model="form.opf_split[0]">
                                                            <template v-slot:label>
                                                                <div :style="$store.state.textFormatting.main">Alerts</div>
                                                            </template> 
                                                        </v-switch>
                                                        <v-switch v-model="form.opf_split[4]" :disabled="!form.opf_split[0]">
                                                            <template v-slot:label>
                                                                <div :style="$store.state.textFormatting.main">Intra Org</div>
                                                            </template> 
                                                        </v-switch>
                                                    </v-flex>
                                                    <v-tooltip top eager close-delay=2000>
                                                        <template  v-slot:activator="{ on }">
                                                            <v-flex v-on="on" shrink class="px-2">
                                                                <v-switch v-model="form.opf_split_e[0]" :disabled="!form.opf_split[0]">
                                                                    <template v-slot:label>
                                                                        <div :style="$store.state.textFormatting.main">Emails</div>
                                                                    </template> 
                                                                </v-switch>
                                                            </v-flex>
                                                        </template>
                                                        <span :style="$store.state.textFormatting.main">Enable emails for <b>this alert type</b>. You will also need to enable emails on the 
                                                            <v-btn small @click="routeNewWindow('User Admin')" color="success" :style="$store.state.textFormatting.main">USER SETTINGS</v-btn>
                                                            page to receive alerts (as emails) to your email address.
                                                        </span>
                                                    </v-tooltip>
                                                    
                                                </v-layout>
                                            </v-flex>

                                            <v-flex align-center justify-center>
                                                <v-layout>
                                                    <v-tooltip top eager close-delay=500 >
                                                        <template  v-slot:activator="{ on }">
                                                            <div slot="activator" v-on="on" :style="$store.state.textFormatting.main"><b>Path Changes?</b></div>
                                                        </template>
                                                        <span :style="$store.state.textFormatting.main">Alerts where the origin (ownership) ASN has <b>NOT</b> changed.</span>
                                                    </v-tooltip>
                                                </v-layout>
                                                <v-layout>
                                                    <v-flex shrink class="px-2">
                                                        <v-switch v-model="form.opf_split[1]">
                                                            <template v-slot:label>
                                                                <div :style="$store.state.textFormatting.main">Alerts</div>
                                                            </template> 
                                                        </v-switch>
                                                    </v-flex>
                                                    <v-tooltip top eager close-delay=2000>
                                                        <template  v-slot:activator="{ on }">
                                                            <v-flex v-on="on" shrink class="px-2">
                                                                <v-switch v-model="form.opf_split_e[1]" :disabled="!form.opf_split[1]">
                                                                    <template v-slot:label>
                                                                        <div :style="$store.state.textFormatting.main">Emails</div>
                                                                    </template> 
                                                                </v-switch>
                                                            </v-flex>
                                                        </template>
                                                        <span :style="$store.state.textFormatting.main">Enable emails for <b>this alert type</b>. You will also need to enable emails on the 
                                                            <v-btn small @click="routeNewWindow('User Admin')" color="success" :style="$store.state.textFormatting.main">USER SETTINGS</v-btn>
                                                            page to receive alerts (as emails) to your email address.
                                                        </span>
                                                    </v-tooltip>
                                                    
                                                </v-layout>
                                            </v-flex>

                                            <v-flex align-center justify-center>
                                                <v-layout>
                                                    <v-tooltip top eager close-delay=500 >
                                                        <template  v-slot:activator="{ on }">
                                                            <div slot="activator" v-on="on" :style="$store.state.textFormatting.main"><b>First Seens?</b></div>
                                                        </template>
                                                        <span :style="$store.state.textFormatting.main">Alerts where there was no origin (ownership) ASN from the given peer i.e. the system has not seen a route to this prefix from the given peer.</span>
                                                    </v-tooltip>
                                                </v-layout>
                                                <v-layout>
                                                    <v-flex shrink class="px-2">
                                                        <v-switch v-model="form.opf_split[2]">
                                                            <template v-slot:label>
                                                                <div :style="$store.state.textFormatting.main">Alerts</div>
                                                            </template> 
                                                        </v-switch>
                                                    </v-flex>
                                                    <v-tooltip top eager close-delay=2000>
                                                        <template  v-slot:activator="{ on }">
                                                            <v-flex v-on="on" shrink class="px-2">
                                                                <v-switch v-model="form.opf_split_e[2]" :disabled="!form.opf_split[2]">
                                                                    <template v-slot:label>
                                                                        <div :style="$store.state.textFormatting.main">Emails</div>
                                                                    </template>
                                                                </v-switch>
                                                            </v-flex>
                                                        </template>
                                                        <span :style="$store.state.textFormatting.main">Enable emails for <b>this alert type</b>. You will also need to enable emails on the 
                                                            <v-btn small @click="routeNewWindow('User Admin')" color="success" :style="$store.state.textFormatting.main">USER SETTINGS</v-btn>
                                                            page to receive alerts (as emails) to your email address.
                                                        </span>
                                                    </v-tooltip>
                                                    
                                                </v-layout>
                                            </v-flex>

                                            <v-flex align-center justify-center>
                                                <v-layout>
                                                    <v-tooltip top eager close-delay=500 >
                                                        <template  v-slot:activator="{ on }">
                                                            <div slot="activator" v-on="on" :style="$store.state.textFormatting.main"><b>AS in path alerts?</b></div>
                                                        </template>
                                                        <span :style="$store.state.textFormatting.main">Alerts where the AS of interest is in the AS path (excluding the peer ASN) e.g. if a path is 1 2 3 4 and you are monitoring AS 3.</span>
                                                    </v-tooltip>
                                                </v-layout>
                                                <v-layout>
                                                    <v-flex shrink class="px-2">
                                                        <v-switch v-model="form.opf_split[3]">
                                                            <template v-slot:label>
                                                                <div :style="$store.state.textFormatting.main">Alerts</div>
                                                            </template> 
                                                        </v-switch>
                                                    </v-flex>

                                                    <v-tooltip top eager close-delay=2000>
                                                        <template  v-slot:activator="{ on }">
                                                            <v-flex v-on="on" shrink class="px-2">
                                                                <v-switch v-model="form.opf_split_e[3]" :disabled="!form.opf_split[3]">
                                                                    <template v-slot:label>
                                                                        <div :style="$store.state.textFormatting.main">Emails</div>
                                                                    </template> 
                                                                </v-switch>
                                                            </v-flex>
                                                        </template>
                                                        <span :style="$store.state.textFormatting.main">Enable emails for <b>this alert type</b>. You will also need to enable emails on the 
                                                            <v-btn small @click="routeNewWindow('User Admin')" color="success" :style="$store.state.textFormatting.main">USER SETTINGS</v-btn>
                                                            page to receive alerts (as emails) to your email address.
                                                        </span>
                                                    </v-tooltip>

                                                    
                                                </v-layout>
                                            </v-flex>

                                        </v-layout>
                                        <br v-if="showAdvanced" />
                                        <v-divider v-if="showAdvanced" />
                                        <br v-if="showAdvanced" />
                                        <v-layout justify-center align-center v-if="showAdvanced">
                                            
                                            <v-flex class="px-5">
                                                <v-layout align-center justify-center>
                                                    <v-flex class="px-1">
                                                        <v-btn class="mx-2" color="red" fab small @click="form.as_group=''"><b>X</b></v-btn>
                                                    </v-flex>
                                                    <v-flex class="px-1">
                                                        <v-select :style="$store.state.textFormatting.main" v-model="form.as_group" :items="useASGroups" item-text="group_name" item-value="group_name" label="AS Group"> </v-select>
                                                    </v-flex>
                                                    <v-flex class="px-1">
                                                        <v-tooltip top eager color="primary" id="exit-app">
                                                            <template v-slot:activator="{ on }">
                                                                <v-btn v-on="on" :style="$store.state.textFormatting.main" color="success" @click="routeNewWindow('Alert Refinement')"> Manage AS Groups </v-btn>
                                                            </template>
                                                            <span :style="$store.state.textFormatting.main">Alert Refinement Page (opens new window)</span>
                                                        </v-tooltip>
                                                    </v-flex>
                                                </v-layout>
                                            </v-flex>

                                            <v-flex class="px-5">
                                                <v-select :style="$store.state.textFormatting.main" v-model="form.type" :items="asnTypes" item-text="name" item-value="value" label="Select type for default thresholds" v-on:change="updateDefs"> </v-select>
                                            </v-flex>
                                            <!--<v-flex class="text-xs-center">
                                                <v-tooltip top eager close-delay=500 >
                                                    <template  v-slot:activator="{ on }">
                                                        <div v-on="on">
                                                            <v-select :style="$store.state.textFormatting.main" slot="activator" v-model="form.alerting_type" :items="alertingTypes" item-text="name" item-value="value" label="Alerting Type"> </v-select>
                                                        </div>
                                                    </template>
                                                    <span :style="$store.state.textFormatting.main">
                                                        <b>Path Based:</b> Where the ASN of interest is present in the AS path (new or old), you will receive alerts 
                                                        <br /> (so long as score threshold settings are satisfied).
                                                        <br /> <b>Origin Based:</b> <i>(subset of <b>path based</b>)</i> Only receive alerts where the ASN of interest is the origin ASN (new or old).
                                                    </span>
                                                </v-tooltip>      
                                            </v-flex>-->
                                            <!--<v-flex justify-center align-center>
                                                <v-tooltip top eager close-delay=500 >
                                                    <template  v-slot:activator="{ on }">
                                                        <v-switch slot="activator" v-on="on" v-model="form.exclude_peer" v-if="form.alerting_type=='path'">
                                                            <template v-slot:label>
                                                                <div :style="$store.state.textFormatting.main">Exclude Peer?</div>
                                                            </template> 
                                                        </v-switch>
                                                    </template>
                                                    <span :style="$store.state.textFormatting.main">For path based alerting, whether to exclude the peer ASN from the path (i.e. if any ASN in the path other than the peer ASN is the ASN of interest, you will be alerted)</span>
                                                </v-tooltip>      
                                            </v-flex>-->
                                            <!--<v-flex>
                                                <v-tooltip top eager close-delay=500 >
                                                    <template  v-slot:activator="{ on }">
                                                        <v-switch slot="activator" v-on="on" v-model="form.emails">
                                                            <template v-slot:label>
                                                                <div :style="$store.state.textFormatting.main">Send emails?</div>
                                                            </template>
                                                        </v-switch>
                                                    </template>
                                                    <span :style="$store.state.textFormatting.main">Whether I'd like to receive emails for alerts from the ASN (<b>NOT CURRENTLY ACTIVE</b>)</span>
                                                </v-tooltip>      
                                            </v-flex>-->
                                            <v-flex class="px-5">
                                                <v-tooltip top eager close-delay=500 >
                                                    <template  v-slot:activator="{ on }">
                                                        <div v-on="on">
                                                            <v-select :style="$store.state.textFormatting.main" slot="activator" v-model="form.and_or" :items="andOr" item-text="name" item-value="value" label="Scoring AND/ OR"> </v-select>
                                                        </div>
                                                    </template>
                                                    <span :style="$store.state.textFormatting.main">
                                                        Let's say we have hijack and impact settings as follows: <b> H = 10, I = 5 </b>
                                                        <br /> An event comes in with <b> H = 15 and I = 2 </b>.
                                                        <br /> <b> And: </b> BOTH hijack and impact scores must exceed thresholds, so in this case I am <b>not alerted</b> (impact not satisfied).
                                                        <br /> <b> Or: </b> EITHER hijack or impact score must exceed thresholds, so I <b>am alerted</b>.
                                                    </span>
                                                </v-tooltip>      
                                            </v-flex>
                                        </v-layout>
                                        <v-layout row v-if="showAdvanced">
                                            <v-flex xs8 class="px-3">
                                                <v-tooltip top eager close-delay=500 >
                                                    <template  v-slot:activator="{ on }">
                                                        <div v-on="on">
                                                            <v-subheader slot="activator" class="pl-0" :style="$store.state.textFormatting.main">Hijack Score</v-subheader>
                                                        </div>
                                                    </template>
                                                    <span :style="$store.state.textFormatting.main">
                                                        <b> The higher you set the threshold here, the less alerts you will receive (i.e. less noise): </b> <br />
                                                        H (0 - 20): Low chance of being hijacking event. <br />
                                                        H (20 - 50): Reasonable chance of hijacking event. <br />
                                                        H (50 +): High chance of hijacking event/ misconfigured router.
                                                    </span>
                                                </v-tooltip>
                                                <v-slider v-model="form.h" thumb-label="always" min="0" max="99"></v-slider>
                                            </v-flex>
                                            <v-flex xs8 class="px-3">
                                                <v-tooltip top eager close-delay=500 >
                                                    <template  v-slot:activator="{ on }">
                                                        <div v-on="on">
                                                            <v-subheader slot="activator" class="pl-0" :style="$store.state.textFormatting.main">Impact Score</v-subheader>
                                                        </div>
                                                    </template>
                                                    <span :style="$store.state.textFormatting.main">
                                                        <b> The higher you set the threshold here, the less alerts you will receive (i.e. less noise): </b> <br />
                                                        I (0 - 8): Low impact (based on ASN old/new geo comparison, subownership assertion scope i.e. how specific advertisment is compared to parent prefix). <br />
                                                        I (8 - 20): Medium impact. <br />
                                                        I (20 - 50): High impact. <br />
                                                        I (50 +): Reserved for <b>restricted prefixes</b> in your organisation.
                                                    </span>
                                                </v-tooltip>
                                                <v-slider v-model="form.i" thumb-label="always" min="0" max="99"></v-slider>
                                            </v-flex>
                                            <v-flex xs8 class="px-3">
                                                <v-tooltip top eager close-delay=500 >
                                                    <template  v-slot:activator="{ on }">
                                                        <div v-on="on">
                                                            <v-subheader slot="activator" class="pl-0" :style="$store.state.textFormatting.main">Repeat Email Alert Threshold (V)</v-subheader>
                                                        </div>
                                                    </template>
                                                    <span :style="$store.state.textFormatting.main">
                                                        How many <b>events</b> it takes for an <b>additional email</b> to be sent out re-alerting you to the ongoing episode. <br />
                                                        e.g. if I set V = 10, I will be sent an initial notifying email, <br /> then once the alert has at least 10 instances of events in it, I will receive an additional email.
                                                    </span>
                                                </v-tooltip>
                                                <v-slider v-model="form.v" thumb-label="always" min="0" max="99"></v-slider>
                                            </v-flex>
                                        </v-layout>
                                    </v-card>
                            </v-form>
                        </v-card-text>
                    </v-container>
                </v-card>
        </v-flex>
        <v-flex xs12 md10 v-if="displayMessage">
            <v-card class="justify-right" :color="msgColor">
                <h3 :style="$store.state.textFormatting.main"> {{ message }} </h3>
            </v-card>
        </v-flex>
        </v-layout>
        <v-dialog v-model="showAreYouSure" width="500">
            <v-toolbar color="secondary" dark>
                <v-toolbar-title :style="$store.state.textFormatting.headers"> ASN {{ deleteASN.asn }} </v-toolbar-title> <v-spacer></v-spacer> <v-icon>edit</v-icon> 
            </v-toolbar>
            <v-card >
                <v-card-text>
                    <h3 :style="$store.state.textFormatting.main"> Are you sure you want to delete AS {{ deleteASN.asn }}?</h3>
                    <br />
                    <h3 :style="$store.state.textFormatting.main" v-if="deleteASN"> 
                        Current settings:
                        <ul>
                            <li :style="$store.state.textFormatting.main" class="text-xs-left"> 
                                <b> Ownership Assertions </b>
                                        <v-icon v-if="deleteASN.opf_split[0]"> check </v-icon>
                                        <v-icon v-if="!deleteASN.opf_split[0]"> clear </v-icon>
                                        <span v-if="deleteASN.opf_split[0]">
                                            (
                                            <v-icon v-if="deleteASN.opf_split_e[0]"> check </v-icon>
                                            <v-icon v-if="!deleteASN.opf_split_e[0]"> clear </v-icon>
                                            )
                                        </span>
                            </li>
                            <li :style="$store.state.textFormatting.main" class="text-xs-left"> 
                                <b> Path Changes </b>
                                        <v-icon v-if="deleteASN.opf_split[1]"> check </v-icon>
                                        <v-icon v-if="!deleteASN.opf_split[1]"> clear </v-icon>
                                        <span v-if="deleteASN.opf_split[1]">
                                            (
                                            <v-icon v-if="deleteASN.opf_split_e[1]"> check </v-icon>
                                            <v-icon v-if="!deleteASN.opf_split_e[1]"> clear </v-icon>
                                            )
                                        </span>
                            </li>
                            <li :style="$store.state.textFormatting.main" class="text-xs-left"> 
                                <b> First Seens </b>
                                        <v-icon v-if="deleteASN.opf_split[2]"> check </v-icon>
                                        <v-icon v-if="!deleteASN.opf_split[2]"> clear </v-icon>
                                        <span v-if="deleteASN.opf_split[2]">
                                            (
                                            <v-icon v-if="deleteASN.opf_split_e[2]"> check </v-icon>
                                            <v-icon v-if="!deleteASN.opf_split_e[2]"> clear </v-icon>
                                            )
                                        </span>
                            </li>
                            <li :style="$store.state.textFormatting.main" class="text-xs-left"> 
                                <b> AS in path alerts </b>
                                        <v-icon v-if="deleteASN.opf_split[3]"> check </v-icon>
                                        <v-icon v-if="!deleteASN.opf_split[3]"> clear </v-icon>
                                        <span v-if="deleteASN.opf_split[3]">
                                            (
                                            <v-icon v-if="deleteASN.opf_split_e[3]"> check </v-icon>
                                            <v-icon v-if="!deleteASN.opf_split_e[3]"> clear </v-icon>
                                            )
                                        </span>
                            </li>
                        </ul>
                    </h3>
                </v-card-text>
                <v-card-actions>
                    <v-btn class="green" dark @click="removeASN" :style="$store.state.textFormatting.main"> Yes, delete</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn class="red" dark @click="showAreYouSure=false" :style="$store.state.textFormatting.main"> No, close </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showCSV" :width="this.$store.state.textFormatting.dialog.b_width" persistent>
            <v-toolbar color="secondary" dark>
                <v-toolbar-title :style="$store.state.textFormatting.headers"> CSV Import ASNs ({{ addType }}) </v-toolbar-title>
                <v-spacer></v-spacer>
                <downloadcsv
                    class   = "btn btn-default"
                    :data   = "csvTemplate"
                    name = "csvTemplate.csv">
                    <v-tooltip top eager color="primary" id="exit-app">
                        <template v-slot:activator="{ on }">
                            <v-btn small dark v-on="on" color="success" :style="$store.state.textFormatting.main"> <v-icon>save_alt</v-icon> Template </v-btn>
                        </template>
                        <span :style="$store.state.textFormatting.main">CSV Template for ASN entry</span>
                    </v-tooltip>        
                </downloadcsv>
                <info>
                    <template>
                        Add multiple ASNs from CSV
                    </template>
                    <template slot="body">
                        <p> Download template CSV file </p>
                        <p> Enter your ASNs into said file, then save (as CSV) </p>
                        <p> Press the 'choose file' button to upload the CSV </p>
                        <p> Check the entries look correct in the table, then click submit </p>
                        <br />
                        <p> <b> NOTE: </b> Please enter the following settings as follows:
                            <ul>
                                <li> <b> alerting_type:</b> path / origin </li>
                                <li> <b> exclude_peer:</b> Y / N </li>
                                <li> <b> emails:</b> Y / N </li>
                                <li> <b> and_or:</b> and / or </li>
                                
                            </ul>
                        </p>
                        <p> <b> NOTE: </b> Non-integer ASNs, hijack, impact and validation scores will be set to 0</p>
                    </template>
                </info>
            </v-toolbar>
            <v-card justify-center align-center>
            
                <br />
                <v-flex justify-center align-center class="text-xs-center">
                    <input type="file" id="csv_file" name="csv_file" class="form-controll" @change="loadCSV($event)">
                </v-flex>
                <br />
                <v-flex>
                    <v-data-table :headers="csvHeaders" v-if="showCSVTable" :items="parse_csv" :key="id" :rows-per-page-items="rowsPerPageItems3" :pagination.sync="pagination3">
                        <template slot="headerCell" slot-scope="props">
                            <v-tooltip top eager>
                            <span slot="activator" :style="$store.state.textFormatting.main">
                                {{ props.header.text }}
                            </span>
                            <span :style="$store.state.textFormatting.main">
                                {{ props.header.ad_text }}
                            </span>
                            </v-tooltip>
                        </template>
                        <template slot="items" slot-scope="props">
                            <tr>
                                <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.asn }} </td>
                                <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.asn_name }} </td>
                                <td :style="$store.state.textFormatting.main" class="text-xs-center"> {{ props.item.as_group }} </td>
                                <td :style="$store.state.textFormatting.main" class="text-xs-center"> 
                                    <v-icon v-if="props.item.opf_split[0]"> check </v-icon>
                                    <v-icon v-if="!props.item.opf_split[0]"> clear </v-icon>
                                    <span v-if="props.item.opf_split[0]">
                                        (
                                        <v-icon v-if="props.item.opf_split[4]"> check </v-icon>
                                        <v-icon v-if="!props.item.opf_split[4]"> clear </v-icon>
                                        <v-icon v-if="props.item.opf_split_e[0]"> check </v-icon>
                                        <v-icon v-if="!props.item.opf_split_e[0]"> clear </v-icon>
                                        )
                                    </span>
                                </td>
                                <td :style="$store.state.textFormatting.main" class="text-xs-center"> 
                                    <v-icon v-if="props.item.opf_split[1]"> check </v-icon>
                                    <v-icon v-if="!props.item.opf_split[1]"> clear </v-icon>
                                    <span v-if="props.item.opf_split[1]">
                                        (
                                        <v-icon v-if="props.item.opf_split_e[1]"> check </v-icon>
                                        <v-icon v-if="!props.item.opf_split_e[1]"> clear </v-icon>
                                        )
                                    </span>
                                </td>
                                <td :style="$store.state.textFormatting.main" class="text-xs-center"> 
                                    <v-icon v-if="props.item.opf_split[2]"> check </v-icon>
                                    <v-icon v-if="!props.item.opf_split[2]"> clear </v-icon>
                                    <span v-if="props.item.opf_split[2]">
                                        (
                                        <v-icon v-if="props.item.opf_split_e[2]"> check </v-icon>
                                        <v-icon v-if="!props.item.opf_split_e[2]"> clear </v-icon>
                                        )
                                    </span>
                                </td>
                                <td :style="$store.state.textFormatting.main" class="text-xs-center"> 
                                    <v-icon v-if="props.item.opf_split[3]"> check </v-icon>
                                    <v-icon v-if="!props.item.opf_split[3]"> clear </v-icon>
                                    <span v-if="props.item.opf_split[3]">
                                        (
                                        <v-icon v-if="props.item.opf_split_e[3]"> check </v-icon>
                                        <v-icon v-if="!props.item.opf_split_e[3]"> clear </v-icon>
                                        )
                                    </span>
                                </td>
                                


                                <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.H }}</td>
                                <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.and_or }}</td>
                                <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.I }}</td>
                                
                                

                                <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.V }}</td>
                                <td class="text-xs-center" v-if="props.item.msg" :style="$store.state.textFormatting.main"> {{ props.item.msg }} </td>
                            </tr>
                        </template>
                        <template slot="no-data">
                            <v-alert :value="true" color="success" icon="warning" :style="$store.state.textFormatting.main">
                                Choose file to populate table
                            </v-alert>
                        </template>
                    </v-data-table>
                </v-flex>
                <v-layout align-center justify-center>
                    <v-flex v-if="csvErr" class="text-xs-center" xs10>
                        <v-card color="success">
                            <h4 :style="$store.state.textFormatting.main"> {{ csvErr }} </h4>
                        </v-card>
                        <br />
                    </v-flex>
                </v-layout>

                <v-layout>
                    <v-btn v-if="csvHeaders.length <= 11" color="red" class="mr-4" @click="csvClose"> Cancel <v-icon> mdi-cancel </v-icon> </v-btn>
                    <v-btn v-if="csvHeaders.length > 11" color="red"  class="mr-4" @click="csvClose"> Close (completed) </v-btn>

                    <v-spacer></v-spacer>
                    <v-btn color="success" class="mr-4" @click="csvSubmit('organisation')" v-if="addType=='organisation'" :disabled="!valid3" :style="$store.state.textFormatting.main"> Submit <v-icon>mdi-checkbox-marked-circle</v-icon>  </v-btn>
                    <v-btn color="success" class="mr-4" @click="csvSubmit('user')" v-if="addType=='user'" :disabled="!valid3" :style="$store.state.textFormatting.main"> Submit <v-icon>mdi-checkbox-marked-circle</v-icon>  </v-btn>
                </v-layout>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showPurge" width="500">
            <v-toolbar color="secondary" dark>
                <v-toolbar-title> Remove all user ASNs </v-toolbar-title> <v-spacer></v-spacer> <v-icon>edit</v-icon> 
            </v-toolbar>
            <v-card >
                <v-card-text>
                    <h3> Are you sure you want to delete all user ASNs? This is not reversible</h3>
                    <h3> It is highly recommended to download a CSV of the ASNs prior to deletion</h3>
                </v-card-text>
                <v-card-actions>
                    <v-btn :disabled="purge_loading" class="green" dark @click="purgeASNs"> Yes, delete</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn class="red" dark @click="showPurge=false"> No, close </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </v-container>
</template>

<script>
import { Auth, API, graphqlOperation } from 'aws-amplify';
import store from '../../store';
import info from '../../shared/operational/infopopout.vue';
import downloadcsv from 'vue-json-csv';
import { findParentComponentByName } from '../utils/findParent.js'
import OldRules from './oldRules.vue'

export default {
    name: 'asnSettings',
    components: {
    info,
    downloadcsv,
    OldRules
},
    describe: 'This page allows Admins to edit the ASNs associated with their organisation, or ASNs they want to keep an eye on for other reasons',
    data() {
        return {
            user_type: "Analyst",
            daysleft: 0,
            valid: false,
            addType: "",
            loading: true,
            deleteASN: 0,
            msgColor: 'success',
            asn_rules: [ v => !! v || 'ASN required', v => /^\d+$/.test(v) || 'ASN must be number ( > 0 )'],
            edType: 'Add',
            showEdit: false,
            showAreYouSure: false,
            rowsPerPageItems: [10, 20, 50, 100],
            pagination: {
                rowsPerPage: 10
            },
            rowsPerPageItems3: [10, 20, 50, 100],
            pagination3: {
                rowsPerPage: 10
            },
            id: 1,
            organisation: '',
            v4rules: [ v => !!v || 'A valid IPv4 address is required', v => /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])(\/([8-9]|[1-2][0-9]|3[0-2]))?$/.test(v) || 'Address must be valid' ],
            v6rules: [ v => !!v || 'A valid IPv6 address required', v => /^s*((([0-9A-Fa-f]{1,4}:){7}([0-9A-Fa-f]{1,4}|:))|(([0-9A-Fa-f]{1,4}:){6}(:[0-9A-Fa-f]{1,4}|((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3})|:))|(([0-9A-Fa-f]{1,4}:){5}(((:[0-9A-Fa-f]{1,4}){1,2})|:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3})|:))|(([0-9A-Fa-f]{1,4}:){4}(((:[0-9A-Fa-f]{1,4}){1,3})|((:[0-9A-Fa-f]{1,4})?:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){3}(((:[0-9A-Fa-f]{1,4}){1,4})|((:[0-9A-Fa-f]{1,4}){0,2}:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){2}(((:[0-9A-Fa-f]{1,4}){1,5})|((:[0-9A-Fa-f]{1,4}){0,3}:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){1}(((:[0-9A-Fa-f]{1,4}){1,6})|((:[0-9A-Fa-f]{1,4}){0,4}:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3}))|:))|(:(((:[0-9A-Fa-f]{1,4}){1,7})|((:[0-9A-Fa-f]{1,4}){0,5}:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3}))|:)))(%.+)?s*(\/(3[2-9]|[4-9][0-9]|1[0-1][0-9]|12[0-8]))?$/.test(v) || 'Address must be valid' ],
            message: "",
            asnHeaders: [
                { text: 'ASN', ad_text:'Autonomous System Number', value: 'asn', align:'centre'},
                { text: 'ASN Name', ad_text:'User defined ASN name (for housekeeping)', value: 'asn_name', align:'centre'},
                { text: 'H', ad_text:'Hijack Score Threshold', value: 'H', align:'centre'},
                { text: 'I', ad_text:'Impact Score Threshold', value: 'I', align:'centre'},
                { text: 'V', ad_text:'Validation Score Threshold', value: 'V', align:'centre'},
                { text: 'Notifications', ad_text:'Count of notifications over last 2 days for given ASN', value: 'notification_count', align:'centre'},
                { text: 'Alert Type', ad_text:'Either path or origin - see info. in top right', value: 'alerting_type', align:'centre'},
                { text: 'Ex. Peer?', ad_text:'Yes / No - see info. in top right', value: 'exclude_peer', align:'centre'},
                { text: 'Send Emails?', ad_text:'Whether the user/ organisation will receive emails based on alerts from this ASN', value: 'emails', align:'centre'},
                { text: 'And/ Or?', ad_text:'To do with scoring mechanism - see info. in top right', value: 'and_or', align:'centre'},
                { text: 'Actions', ad_text:'Edit or delete the ASN', align:'centre', sortable: false}
            ],
            form: {
                asn: 0,
                h: 0,
                i: 0,
                v: 0,
                peering: false,
                asn_name: '',
                type: 0,
                alerting_type: 'origin',
                exclude_peer: true,
                emails: false,
                and_or: 'and',
                as_group: null,
                opf_split: [
                    false,
                    false,
                    false,
                    false
                ],
                opf_split_e: [
                    false,
                    false,
                    false,
                    false
                ]
            },
            selectedASN: {
                asn: 0,
                h: 0,
                i: 0,
                v: 0,
                peering: false,
                asn_name: '',
                type: 0,
                alerting_type: 'origin',
                exclude_peer: true,
                emails: false,
                and_or: 'and',
                own_path_first: '1000',
                emails_opf: '0000',
                as_group: ''
            },
            alertingTypes: [
                {name: "Path based", value: 'path'},
                {name: "Origin ASN based", value: 'origin'},
            ],
            andOr: [
                {name: "And", value: 'and'},
                {name: "Or", value: 'or'},
            ],
            orgASNs: [],
            userASNs: [],
            asnTypes: [
                {name: "Regular", value: 0},
                {name: "High Importance", value: 1},
                {name: "Low Importance", value: 2},
                {name: "Muted", value: 3},
                {name: "Show All", value: 4}
            ],
            asnInvTypes: {
                0: 'Regular',
                1: 'High Importance',
                2: 'Low Importance',
                3: 'Muted',
                4: 'Show All'
            },
            asnFlipInvTypes: {
                'Regular': 0,
                'High Importance': 1,
                'Low Importance': 2,
                'Muted': 3,
                'Show All': 4
            },
            asnMappings: {
                0: {h: 30, i: 10, v: 5},
                1: {h: 20, i: 8, v: 2},
                2: {h: 50, i: 25, v: 10},
                3: {h: 99, i: 99, v: 99},
                4: {h: 0, i: 0, v: 0}
            },
            displayMessage: false,
            username: '',
            loading2: false,
            parse_header: [],
            parse_csv: [],
            sortOrders: {},
            sortKey: '',
            showCSV: false,
            valid3: false,
            csvHeaders: [
                    { text: 'ASN', ad_text:'Autonomous System Number', value: 'asn', align:'centre'},
                    { text: 'ASN Name', ad_text:'User defined ASN name (for housekeeping)', value: 'asn_name', align:'centre'},
                    { text: 'AS Group', ad_text:'Define AS groups to omit / tweak alerts that occur between certain ASNs', value: 'as_group', align:'centre'},
                    { text: 'Own. Assertions?', ad_text:'Whether you will be alerted upon ownership assertions for this ASN (and receive emails on such alerts)', value: 'opf_split[0]', align:'centre'},
                    { text: 'Path Changes?', ad_text:'Whether you will be alerted on path change events for this ASN (and receive emails on such alerts)', value: 'opf_split[1]', align:'centre'},
                    { text: 'First Seens?', ad_text:'Whether you will be alerted upon first seen prefix events or not (and receive emails on such alerts)', value: 'opf_split[2]', align:'centre'},
                    { text: 'AS In Path Alerts?', ad_text:'Whether you will be alerted upon alerts with this AS in the path - excluding this AS as the peer (and receive emails on such alerts)', value: 'opf_split[3]', align:'centre'},
                    { text: 'H', ad_text:'Hijack Score Threshold', value: 'H', align:'centre'},
                    { text: 'And/ Or?', ad_text:'To do with scoring mechanism - see info. in top right', value: 'and_or', align:'centre'},
                    { text: 'I', ad_text:'Impact Score Threshold', value: 'I', align:'centre'},
                    //{ text: 'Alert Type', ad_text:'Either path or origin - see info. in top right', value: 'alerting_type', align:'centre'},
                    //{ text: 'Ex. Peer?', ad_text:'Yes / No - see info. in top right', value: 'exclude_peer', align:'centre'},
                    //{ text: 'Send Emails?', ad_text:'Whether the user/ organisation will receive emails based on alerts from this ASN', value: 'emails', align:'centre'},
                    
                    { text: 'Email Threshold (V)', ad_text:'The amount of events required for an additional email alert to be sent out', value: 'V', align:'centre'},
            ],
            correctHeaders: [
                'asn',
                'asn_name',
                'as_group',
                'own_assertions',
                'intra_org_assertions',
                'own_assertions_emails',
                'path_changes',
                'path_changes_emails',
                'first_seens',
                'first_seens_emails',
                'as_in_path_alerts',
                'as_in_path_alerts_emails',
                'h',
                'i',
                'v',
                'and_or'
            ],
            csvTemplate: [
                {
                    'asn': 'number',
                    'asn_name': 'string (no quotation marks - they will be removed)',
                    'as_group': 'string - note if the group doesn\'t exist this wont work',
                    'own_assertions': 'Y/N, TRUE/FALSE, y/n or 1/0',
                    'intra_org_assertions': 'Y/N, TRUE/FALSE, y/n or 1/0',
                    'own_assertions_emails': 'Y/N, TRUE/FALSE, y/n or 1/0',
                    'path_changes': 'Y/N, TRUE/FALSE, y/n or 1/0',
                    'path_changes_emails': 'Y/N, TRUE/FALSE, y/n or 1/0',
                    'first_seens': 'Y/N, TRUE/FALSE, y/n or 1/0',
                    'first_seens_emails': 'Y/N, TRUE/FALSE, y/n or 1/0',
                    'as_in_path_alerts': 'Y/N, TRUE/FALSE, y/n or 1/0',
                    'as_in_path_alerts_emails': 'Y/N, TRUE/FALSE, y/n or 1/0',
                    'h': '0-99',
                    'i': '0-99',
                    'v': '0-99',
                    'and_or': 'and/or'
                }
            ],
            csvErr: null,
            showCSVTable: true,
            showOrgASNs: true,
            showUserASNs: true,
            asn_own_dict: {},
            asn_get_owner: [],
            is_asn_owners: false,
            showAdvanced: false,
            orgASGroups: [],
            userASGroups: [],
            useASGroups: [],
            showPurge: false,
            purge_loading: false,
            user_download_asns: [],
            organisation_download_asns: []
        } 
    },
    beforeMount () {
        let app = findParentComponentByName(this, 'app')
        app.checkSignedIn()
        var countDownDate = new Date("Mar 14, 2022 09:00:00").getTime();
        var now = new Date().getTime();
        var distance = countDownDate - now;
        var days = Math.floor(distance / (1000 * 60 * 60 * 24));
        this.daysleft=days

        try {
            this.organisation = this.$store.state.user.organisation;
            this.username = this.$store.state.user.username;
        } catch (err) {
            console.log(err)
            location.reload()
        }
        this.getUserType()
        this.getASNs();
        this.getUserASNs();
        // this.pulASNS = this.$store.state.allASNInfoForUser;
        //var a = this.$store.state.allASNInfoForUser;
        /* for (var i=0; i< a.length; i=i+1){
             this.asn.push(a[i].asn)
        } */
        

        if (this.$store.state.orgASGroups) {
           // console.log("pulling org AS groups from store")
            this.orgASGroups = this.$store.state.orgASGroups
        } else {
           // console.log("gathering org AS groups")
            this.getOrgGroups();
        }

        if (this.$store.state.userASGroups) {
          //  console.log("pulling user AS groups from store")
            this.userASGroups = this.$store.state.userASGroups
        } else {
          //  console.log("gathering user AS groups")
            this.getUserGroups();
        }

        this.showAdvanced = this.$store.state.showSettings.advancedSettings;
        this.advancedChange();
    },
    methods: {
        getUserType: async function () {
            const signedin = await Auth.currentAuthenticatedUser();
            this.user_type=signedin.attributes.name;
            console.log(this.user_type);
        },
        purgeASNs: async function () {
            this.purge_loading = true;
            try {
                var query = `
                    mutation purgeASNs {
                        purgeASNs (
                            p_user: "` + this.username + `",
                            p_organisation: "` + this.organisation + `"
                        ) {
                            response
                        }
                    }
                `;
              //  console.log(query)
                const  responseObj  = await API.graphql(graphqlOperation(query));
                this.purge_loading = false
                this.showPurge = false
                this.getUserASNs();
            } catch (e) {
                console.log(e)
                this.purge_loading = false
                this.showPurge = false
                this.getUserASNs();
            }
        },
        routeNewWindow: async function (input) {
            let routeData = this.$router.resolve({name: input});
            window.open(routeData.href, '_blank');
        },
        getOrgGroups: async function () {
            //this.loading = true;

            var query = `query getASNGroups {
                getASNGroups (
                    p_organisation: "` + this.organisation + `",
                    p_user: "whole_organisation"
                ) {
                    group_name,
                    exclude,
                    hijack_setting
                }
            }`;

            const groupData = await API.graphql(graphqlOperation(query));
          //  console.log(groupData)
            var groups = groupData.data.getASNGroups;
          //  console.log(groups)

            if ( !groups ) {
                this.orgASGroups = []
                this.$store.commit("setOrgASGroups", []);
                //this.loading = false
                //this.showOrgGroups = true
            } else if ( groups.length == 0 ) {
                this.orgASGroups = []
                this.$store.commit("setOrgASGroups", []);
                //this.loading = false
                //this.showOrgGroups = true
            } else {
                for (var i = 0 ; i < groups.length ; i++ ) {
                    groups[i].exclude_hij = groups[i].exclude == 1 ? 'Exclude' : 'Hijack' //if 1, its hijack score based else it's exclusion based
                }
                this.orgASGroups = groups;
                this.$store.commit("setOrgASGroups", groups);
                //this.loading = false;
                //this.showOrgGroups = true
            }

        },
        getUserGroups: async function () {
            //this.loading2 = true;

            var query = `query getASNGroups {
                getASNGroups (
                    p_organisation: "` + this.organisation + `",
                    p_user: "` + this.username + `"
                ) {
                    group_name,
                    exclude,
                    hijack_setting
                }
            }`;

            const groupData = await API.graphql(graphqlOperation(query));
           // console.log(groupData)
            var groups = groupData.data.getASNGroups;
           // console.log(groups)

            if ( !groups ) {
                this.userASGroups = []
                this.$store.commit("setUserASGroups", []);
                //this.loading2 = false
                //this.showUserGroups = true
            } else if ( groups.length == 0 ) {
                this.userASGroups = []
                this.$store.commit("setUserASGroups", []);
                //this.loading2 = false
                //this.showUserGroups = true
            } else {
                for (var i = 0 ; i < groups.length ; i++ ) {
                    groups[i].exclude_hij = groups[i].exclude == 1 ? 'Exclude' : 'Hijack' //if 1, its hijack score based else it's exclusion based
                }
                this.userASGroups = groups;
                this.$store.commit("setUserASGroups", groups);
                //this.loading2 = false;
                //this.showUserGroups = true
            }

        },
        advancedChange: async function () {
            if (this.showAdvanced) {
                this.asnHeaders = [
                    { text: 'ASN', ad_text:'Autonomous System Number', value: 'asn', align:'centre'},
                    { text: 'ASN Name', ad_text:'User defined ASN name (for housekeeping)', value: 'asn_name', align:'centre'},
                    { text: 'AS Group', ad_text:'Define AS groups to omit / tweak alerts that occur between certain ASNs', value: 'as_group', align:'centre'},
                    { text: 'Own. Assertions?', ad_text:'Whether you will be alerted upon ownership assertions for this ASN (and receive emails on such alerts)', value: 'opf_split[0]', align:'centre'},
                    { text: 'Path Changes?', ad_text:'Whether you will be alerted on path change events for this ASN (and receive emails on such alerts)', value: 'opf_split[1]', align:'centre'},
                    { text: 'First Seens?', ad_text:'Whether you will be alerted upon first seen prefix events or not (and receive emails on such alerts)', value: 'opf_split[2]', align:'centre'},
                    { text: 'AS In Path Alerts?', ad_text:'Whether you will be alerted upon alerts with this AS in the path - excluding this AS as the peer (and receive emails on such alerts)', value: 'opf_split[3]', align:'centre'},
                    { text: 'H', ad_text:'Hijack Score Threshold', value: 'H', align:'centre'},
                    { text: 'And/ Or?', ad_text:'To do with scoring mechanism - see info. in top right', value: 'and_or', align:'centre'},
                    { text: 'I', ad_text:'Impact Score Threshold', value: 'I', align:'centre'},
                    //{ text: 'Alert Type', ad_text:'Either path or origin - see info. in top right', value: 'alerting_type', align:'centre'},
                    //{ text: 'Ex. Peer?', ad_text:'Yes / No - see info. in top right', value: 'exclude_peer', align:'centre'},
                    //{ text: 'Send Emails?', ad_text:'Whether the user/ organisation will receive emails based on alerts from this ASN', value: 'emails', align:'centre'},
                    
                    { text: 'Email Threshold (V)', ad_text:'The amount of events required for an additional email alert to be sent out', value: 'V', align:'centre'},
                    { text: 'Notifications', ad_text:'Count of notifications over last 2 days for given ASN', value: 'notification_count', align:'centre'},
                    { text: 'Actions', ad_text:'Edit or delete the ASN', align:'centre', sortable: false}
                ]
            } else {
                this.asnHeaders = [
                    { text: 'ASN', ad_text:'Autonomous System Number', value: 'asn', align:'centre'},
                    { text: 'ASN Name', ad_text:'User defined ASN name (for housekeeping)', value: 'asn_name', align:'centre'},
                    //{ text: 'AS Group', ad_text:'Define AS groups to omit / tweak alerts that occur between certain ASNs', value: 'as_group', align:'centre'},
                    //{ text: 'Own. Assertions?', ad_text:'Whether you will be alerted upon ownership assertions for this ASN (and receive emails on such alerts)', value: 'opf_split[0]', align:'centre'},
                    //{ text: 'Path Changes?', ad_text:'Whether you will be alerted on path change events for this ASN (and receive emails on such alerts)', value: 'opf_split[1]', align:'centre'},
                    //{ text: 'First Seens?', ad_text:'Whether you will be alerted upon first seen prefix events or not (and receive emails on such alerts)', value: 'opf_split[2]', align:'centre'},
                    //{ text: 'AS In Path Alerts?', ad_text:'Whether you will be alerted upon alerts with this AS in the path - excluding this AS as the peer (and receive emails on such alerts)', value: 'opf_split[3]', align:'centre'},
                    //{ text: 'H', ad_text:'Hijack Score Threshold', value: 'H', align:'centre'},
                    //{ text: 'I', ad_text:'Impact Score Threshold', value: 'I', align:'centre'},
                    //{ text: 'Alert Type', ad_text:'Either path or origin - see info. in top right', value: 'alerting_type', align:'centre'},
                    //{ text: 'Ex. Peer?', ad_text:'Yes / No - see info. in top right', value: 'exclude_peer', align:'centre'},
                    //{ text: 'Send Emails?', ad_text:'Whether the user/ organisation will receive emails based on alerts from this ASN', value: 'emails', align:'centre'},
                    //{ text: 'And/ Or?', ad_text:'To do with scoring mechanism - see info. in top right', value: 'and_or', align:'centre'},
                    //{ text: 'V', ad_text:'Validation Score Threshold', value: 'V', align:'centre'},
                    { text: 'Notifications', ad_text:'Count of notifications over last 2 days for given ASN', value: 'notification_count', align:'centre'},
                    { text: 'Actions', ad_text:'Edit or delete the ASN', align:'centre', sortable: false}
                ]
            }
        },
        csvOpen: function () {
            this.parse_header = []
            this.parse_csv = []
            this.valid3 = false
            this.csvErr = null
            this.showCSV = true
        },
        csvClose: function () {
            this.showCSV = false
        },
        csvSubmit: async function (type) {
            this.valid3 = false
            if (type == 'user') {
                var p_user = this.$store.state.user.username
            } else {
                var p_user = 'whole_organisation'
            }
            for ( var i = 0 ; i < this.parse_csv.length ; i++ ){
                var entry = this.parse_csv[i]
                if (entry.write == true) { 
                    try{
                        var query = `mutation addASN { updateASN (
                            asn: "`+entry.asn+`", 
                            H: `+entry.h+`, 
                            I: `+entry.i+`, 
                            V: `+entry.v+`, 
                            peering_asn: "N",
                            asn_class: 0, 
                            organisation: "`+this.organisation + `",
                            p_user: "` + p_user + `",
                            p_as_group: "` + entry.as_group + `",
                            p_own_path_first: "` + entry.own_path_first + `",
                            p_emails_opf: "` + entry.emails_opf + `",
                            p_and_or: "` + entry.and_or + `",
                            p_asn_name: "` + entry.asn_name + `",
                            o_and_or: "` + entry.and_or + `",
                            o_own_path_first: "` + entry.own_path_first + `",
                            o_emails_opf: "` + entry.emails_opf + `",
                            o_h: ` + entry.h + `,
                            o_i: `+ entry.i +`,
                            o_v: `+ entry.v + `
                        ) {response}}`

                      //  console.log(query);
                        const updateASNSettings = await API.graphql(graphqlOperation(query));
                      //  console.log(updateASNSettings);
                        var responseMessage = updateASNSettings.data.updateASN;
                        
                        // EDIT THIS
                        if (responseMessage) {
                            var t_msg = responseMessage[0].response
                        } else {
                            var t_msg = "Error editting AS " + entry.asn;
                        }
                        entry.msg = t_msg
                        this.showCSVTable = false
                        this.$nextTick(() => {
                        // Add the component back in
                        this.showCSVTable = true;
                        });

                    } catch (err) {
                        entry.msg = err
                        this.showCSVTable = false
                        this.$nextTick(() => {
                        // Add the component back in
                        this.showCSVTable = true;
                        });
                    }
                    

                    

                }
                if (i == this.parse_csv.length - 1) {
                    if (this.addType == "user") {
                        this.getUserASNs();
                    } else {
                        this.getASNs();
                    }
                }
            }
            this.csvHeaders.push({
                text: 'Posted', ad_text:'Did it post successfully?', value: 'msg', align:'centre'
            })
        },
        testCSVInput: function () {
            var checker;
            var reason;
            var csvmsg = '';
            var total_write = 0;

            var as_group_list = [];
            if (this.addType == 'user') {
                for ( var j = 0 ; j < this.userASGroups.length ; j ++ ) {
                    as_group_list.push(this.userASGroups[j].group_name)
                }
            } else {
                for ( var j = 0 ; j < this.orgASGroups.length ; j ++ ) {
                    as_group_list.push(this.orgASGroups[j].group_name)
                }
            }
           // console.log(as_group_list)


           // console.log("starting CSV Input validation...")
            for (var i = 0; i < this.parse_csv.length ; i++) {
                checker = 0
                var entry = this.parse_csv[i]
              //  console.log(entry)
                if (entry.asn != '') {
                    try {
                        var temp_n = parseInt(entry.asn)
                        if (Number.isInteger(temp_n)) {
                            entry.asn = temp_n
                        } else {
                            checker = 1
                            reason = 'ASN not a number, or cannot be converted to a number'
                        }
                    } catch {
                        checker = 1
                        reason = 'ASN not a number, or cannot be converted to a number'
                    }
                } else {
                    checker = 1
                    reason = "No ASN entered"
                }

                if (checker == 1) {
                    entry.write = false
                    var addstr = "entry " + (i + 1) + " will not be written - " + reason + ". "
                    csvmsg += addstr
                } else {
                    entry.write = true
                    total_write += 1
                    
                    
                
                    if (entry.h == '') {
                        entry.h = 0
                    } else {
                        try {
                            var temp_n = parseInt(entry.h)
                            if (Number.isInteger(temp_n)) {
                                
                                if (temp_n >= 0 && temp_n <= 99) {
                                    entry.h = temp_n
                                } else {
                                    if (temp_n > 99) {
                                        entry.h = 99
                                    } else {
                                        entry.h = 0
                                    }
                                }
                            } else {
                                entry.h = 0
                            }
                        } catch {
                            entry.h = 0
                        }
                    }

                    if (entry.i == '') {
                        entry.i = 0
                    } else {
                        try {
                            var temp_n = parseInt(entry.i)
                            if (Number.isInteger(temp_n)) {
                                
                                if (temp_n >= 0 && temp_n <= 99) {
                                    entry.i = temp_n
                                } else {
                                    if (temp_n > 99) {
                                        entry.i = 99
                                    } else {
                                        entry.i = 0
                                    }
                                }
                            } else {
                                entry.i = 0
                            }
                        } catch {
                            entry.i = 0
                        }
                    }
                
                    if (entry.v == '') {
                        entry.v = 0
                    } else {
                        try {
                            var temp_n = parseInt(entry.v)
                            if (Number.isInteger(temp_n)) {
                                
                                if (temp_n >= 0 && temp_n <= 99) {
                                    entry.v = temp_n
                                } else {
                                    if (temp_n > 99) {
                                        entry.v = 99
                                    } else {
                                        entry.v = 0
                                    }
                                }
                            } else {
                                entry.v = 0
                            }
                        } catch {
                            entry.v = 0
                        }
                    }
                
                    /*var al_types = ['path', 'origin']
                    if (!(al_types.includes(entry.alerting_type))) {
                        entry.alerting_type = 'origin'
                    }
                    var y_n = ['Y', 'N']
                    if (!(y_n.includes(entry.exclude_peer))) {
                        entry.exclude_peer = 'N'
                    }
                    if (!(y_n.includes(entry.emails))) {
                        entry.emails = 'N'
                    }*/
                    try {
                        var a_o = ['and', 'or']
                        if (!(a_o.includes(entry.and_or))) {
                            entry.and_or = 'and'
                        }
                    } catch (e) {
                        console.log(e)
                        entry.and_or = 'and'
                    }



                    try {
                        if ( !(as_group_list.includes(entry.as_group))) {
                            entry.as_group = ''
                        }
                    } catch (e) {
                        console.log(e)
                        entry.as_group = ''
                    }


                    // ALERTING SPLIT OUT BIT
                   // console.log("alert split bit...")

                    entry.own_path_first = ""
                    entry.opf_split = [true, false, false, false, false]
                    try {
                        if (entry.own_assertions == '') {
                            entry.own_path_first += '1' // THIS IS THE ONLY ONE WE DEFAULT TO YES
                        } else {
                            if (entry.own_assertions == 'TRUE' || entry.own_assertions == 'Y' || entry.own_assertions == 'y' || entry.own_assertions == 'true' || entry.own_assertions == 1  || entry.own_assertions == '1') {
                                entry.own_path_first += '1'

                            } else {
                                entry.own_path_first += '0'
                                entry.opf_split[0] = false
                            }
                        }
                    } catch (e) {
                        console.log(e)
                        if (entry.own_path_first.length == 0) {
                            entry.own_path_first += '1' // THIS IS THE ONLY ONE WE DEFAULT TO YES
                        }
                    }

                    try {
                        if (entry.path_changes == '') {
                            entry.own_path_first += '0'
                        } else {
                            if (entry.path_changes == 'TRUE' || entry.path_changes == 'Y' || entry.path_changes == 'y' || entry.path_changes == 'true' || entry.path_changes == 1  || entry.path_changes == '1') {
                                entry.own_path_first += '1'
                                entry.opf_split[1] = true
                            } else {
                                entry.own_path_first += '0'
                            }
                        }
                    } catch (e) {
                        console.log(e)
                        if (entry.own_path_first.length == 1) {
                            entry.own_path_first += '0'
                        }
                    }

                    try {
                        if (entry.first_seens == '') {
                            entry.own_path_first += '0'
                        } else {
                            if (entry.first_seens == 'TRUE' || entry.first_seens == 'Y' || entry.first_seens == 'y' || entry.first_seens == 'true' || entry.first_seens == 1  || entry.first_seens == '1') {
                                entry.own_path_first += '1'
                                entry.opf_split[2] = true
                            } else {
                                entry.own_path_first += '0'
                            }
                        }
                    } catch (e) {
                        console.log(e)
                        if (entry.own_path_first.length == 2) {
                            entry.own_path_first += '0'
                        }
                    }

                    try {
                        if (entry.as_in_path_alerts == '') {
                            entry.own_path_first += '0'
                        } else {
                            if (entry.as_in_path_alerts == 'TRUE' || entry.as_in_path_alerts == 'Y' || entry.as_in_path_alerts == 'y' || entry.as_in_path_alerts == 'true' || entry.as_in_path_alerts == 1  || entry.as_in_path_alerts == '1') {
                                entry.own_path_first += '1'
                                entry.opf_split[3] = true
                            } else {
                                entry.own_path_first += '0'
                            }
                        }
                    } catch (e) {
                        console.log(e)
                        if (entry.own_path_first.length == 3) {
                            entry.own_path_first += '0'
                        }
                    }
//////////////////////////////////////////////
                    try {
                       // console.log("testing intra-org")
                        if (entry.intra_org_assertions == '') {
                            entry.own_path_first += '0'
                       //     console.log("intra-org empty")
                        } else {
                       //     console.log("intra-org not empty")
                            if (entry.intra_org_assertions == 'TRUE' || entry.intra_org_assertions == 'Y' || entry.intra_org_assertions == 'y' || entry.intra_org_assertions == 'true' || entry.intra_org_assertions == 1  || entry.intra_org_assertions == '1') {
                                entry.own_path_first += '1'
                                entry.opf_split[4] = true
                       //         console.log("intra-org set 1")
                            } else {
                                entry.own_path_first += '0'
                       //         console.log("intra-org set 0")
                            }
                        }
                    } catch (e) {
                        console.log(e)
                        if (entry.own_path_first.length == 3) {
                            entry.own_path_first += '0'
                        }
                    }
////////////////////////////////////////////////
                    // EMAILS SPLIT OUT BIT
                   // console.log("emails split bit...")

                    entry.emails_opf = ""
                    entry.opf_split_e = [false, false, false, false, false]
                   // console.log("e1")
                    try {
                        if (entry.own_assertions_emails == '') {
                            entry.emails_opf += '0'
                        } else {
                            if (entry.own_assertions_emails == 'TRUE' || entry.own_assertions_emails == 'Y' || entry.own_assertions_emails == 'y' || entry.own_assertions_emails == 'true' || entry.own_assertions_emails == 1  || entry.own_assertions_emails == '1') {
                                entry.emails_opf += '1'
                                entry.opf_split_e[0] = true
                            } else {
                                entry.emails_opf += '0'
                            }
                        }
                    } catch (e) {
                        console.log(e)
                        if (entry.emails_opf.length == 0) {
                            entry.emails_opf += '0'
                        }
                    }

                  //  console.log("e2")
                    try {
                        if (entry.path_changes_emails == '') {
                            entry.emails_opf += '0'
                        } else {
                            if (entry.path_changes_emails == 'TRUE' || entry.path_changes_emails == 'Y' || entry.path_changes_emails == 'y' || entry.path_changes_emails == 'true' || entry.path_changes_emails == 1  || entry.path_changes_emails == '1') {
                                entry.emails_opf += '1'
                                entry.opf_split_e[1] = true
                            } else {
                                entry.emails_opf += '0'
                            }
                        }
                    } catch (e) {
                        console.log(e)
                        if (entry.emails_opf.length == 1) {
                            entry.emails_opf += '0'
                        }
                    }

                  //  console.log("e3")
                    try {
                        if (entry.first_seens_emails == '') {
                            entry.emails_opf += '0'
                        } else {
                            if (entry.first_seens_emails == 'TRUE' || entry.first_seens_emails == 'Y' || entry.first_seens_emails == 'y' || entry.first_seens_emails == 'true' || entry.first_seens_emails == 1  || entry.first_seens_emails == '1') {
                                entry.emails_opf += '1'
                                entry.opf_split_e[2] = true
                            } else {
                                entry.emails_opf += '0'
                            }
                        }
                    } catch (e) {
                        console.log(e)
                        if (entry.emails_opf.length == 2) {
                            entry.emails_opf += '0'
                        }
                    }

                  //  console.log("e4")
                    try {
                        if (entry.as_in_path_alerts_emails == '') {
                            entry.emails_opf += '0'
                        } else {
                            if (entry.as_in_path_alerts_emails == 'TRUE' || entry.as_in_path_alerts_emails == 'Y' || entry.as_in_path_alerts_emails == 'y' || entry.as_in_path_alerts_emails == 'true' || entry.as_in_path_alerts_emails == 1  || entry.as_in_path_alerts_emails == '1') {
                                entry.emails_opf += '1'
                                entry.opf_split_e[3] = true
                            } else {
                                entry.emails_opf += '0'
                            }
                        }
                    } catch (e) {
                        console.log(e)
                        if (entry.emails_opf.length == 3) {
                            entry.emails_opf += '0'
                        }
                    }

                }
            }
            
            if( csvmsg != '') {
                this.csvErr = csvmsg;
            } else {
                this.csvErr = "All entries will be written"
            }

            if (total_write > 0) {
                this.valid3 = true
            }
        },
        sortBy: function (key) {
            var vm = this
            vm.sortKey = key
            vm.sortOrders[key] = vm.sortOrders[key] * -1
        },
        csvJSON(csv){
            var vm = this
            var lines = csv.split("\n")
            var result = []
            var headers = lines[0].split(",")
            vm.parse_header = lines[0].split(",") 
            lines[0].split(",").forEach(function (key) {
                vm.sortOrders[key] = 1
            })
            
            lines.map(function(line, indexLine){
                if (indexLine < 1) return // Jump header line
                
                var obj = {}
                var currentline = line.split(",")
                if (line.length>1) {
                  //  console.log(line)
                    headers.map(function(header, indexHeader){
                        obj[header.replace(/^\s+|\s+$/g, '')] = currentline[indexHeader]
                    })
                    
                    result.push(obj)
                }
            })
          //  console.log("done")            
            try {
                result[result.length - 1].asn
            } catch (e) {
                console.log(e)
                result.pop() // remove the last item because undefined values
            }
            
            return result // JavaScript object
        },
        loadCSV(e) {
            var vm = this;
            vm.valid3 = false;
          //  console.log("reading csv...")
            if (window.FileReader) {
                var reader = new FileReader();
                reader.readAsText(e.target.files[0]);
                // Handle errors load
                reader.onload = function(event) {
                    var csv = event.target.result;
                    try {
                        vm.parse_csv = vm.csvJSON(csv)
                    }
                    catch (e)
                    {
                        console.log(e)
                    }
                  //  console.log(vm.parse_csv.length)
                    if (vm.parse_csv.length > 0) {
                  //      console.log("testing CSV input...")
                        vm.testCSVInput()
                    }
                  //  console.log("finished test")
                };
                reader.onerror = function(evt) {
                    if(evt.target.error.name == "NotReadableError") {
                        alert("Cannot read file!");
                    }
                };
                
            } else {
                alert('FileReader are not supported in this browser.');
            }
            
        },
        clickASN: function (asn, asn_name, h, i, v, peering, type, org_user, alerting_type, exclude_peer, emails, and_or, as_group, opf_split, opf_split_e, own_path_first, emails_opf) {
            
            this.addType = org_user;
            if (org_user == 'user') {
                this.useASGroups = this.userASGroups
            } else {
                this.useASGroups = this.orgASGroups
            }
           // console.log(org_user)


            this.clearMessage();

            if (asn == "Click here to add ASN") {
                this.edType = "Add";
                this.valid = false;
                
                this.selectedASN.asn = "";
                this.selectedASN.asn_name = "";
                this.selectedASN.h = 99;
                this.selectedASN.i = 99;
                this.selectedASN.v = 99;
                //this.selectedASN.peering = false;
                //this.selectedASN.type = 4;
                //this.selectedASN.alerting_type = "origin";
                //this.selectedASN.exclude_peer = true;
                //this.selectedASN.emails = false;
                this.selectedASN.and_or = "and";
                this.selectedASN.as_group = null;
                this.selectedASN.own_path_first = '1000';
                this.selectedASN.emails_opf = '0000';
                

                this.form.asn = "";
                this.form.asn_name = "";
                this.form.h = 0;
                this.form.i = 0;
                this.form.v = 0;
                this.form.peering = false;
                this.form.type = 4;
                this.form.alerting_type = "origin";
                this.form.exclude_peer = true;
                this.form.emails = false;
                this.form.and_or = "and";
                this.form.as_group = '';
                this.form.opf_split = [
                    true,
                    false,
                    false,
                    false,
                    false
                ];
                this.form.opf_split_e = [
                    false,
                    false,
                    false,
                    false,
                    false
                ]
            } else {
                this.edType = "Edit";

                //this.selectedASN.asn = "";
                //this.selectedASN.asn_name = "";
                this.selectedASN.h = h;
                this.selectedASN.i = i;
                this.selectedASN.v = v;
                //this.selectedASN.peering = false;
                //this.selectedASN.type = 4;
                //this.selectedASN.alerting_type = "origin";
                //this.selectedASN.exclude_peer = true;
                //this.selectedASN.emails = false;
                this.selectedASN.and_or = and_or;
                this.selectedASN.as_group = as_group;
                this.selectedASN.own_path_first = own_path_first;
                this.selectedASN.emails_opf = emails_opf;

                this.form.asn = asn;
                this.form.asn_name = asn_name;
                this.form.h = h;
                this.form.i = i;
                this.form.v = v;
                if (peering == "Y") {
                    this.form.peering = true;
                } else {
                    this.form.peering = false;
                }
                this.form.type = type;
                this.form.alerting_type = alerting_type;
                if (exclude_peer == "Y") {
                    this.form.exclude_peer = true;
                } else {
                    this.form.exclude_peer = false;
                }
                if (emails == "Y") {
                    this.form.emails = true;
                } else {
                    this.form.emails = false;
                }
                this.form.and_or = and_or;
                this.form.as_group = as_group;
                this.form.opf_split = opf_split;
                this.form.opf_split_e = opf_split_e;
            }

            this.showEdit = true;
            window.scrollTo(0, document.body.scrollHeight || document.documentElement.scrollHeight);
            this.$nextTick(() => {
                // Add the component back in
                window.scrollTo(0, document.body.scrollHeight || document.documentElement.scrollHeight);
            });

        },
        updateDefs: async function() {
            this.form.h = this.asnMappings[this.form.type]['h'];
            this.form.i = this.asnMappings[this.form.type]['i'];
            this.form.v = this.asnMappings[this.form.type]['v'];

        },
        editASN: async function() {
            if (this.$refs.form.validate()) {
                var a;
                var ex_peer;
                var emls;
                if (this.addType == "user") {
                    var p_user = this.$store.state.user.username;
                } else {
                    var p_user = "whole_organisation"
                }
              //  console.log(p_user + " : " + this.addType)
                if (this.form.peering == true) {
                    a = "Y"
                } else {
                    a ="N"
                }

                if (this.form.exclude_peer == true) {
                    ex_peer = "Y"
                } else {
                    ex_peer ="N"
                }

                if (this.form.emails == true) {
                    emls = "Y"
                } else {
                    emls ="N"
                }

                try {
                    if (this.form.asn_name != '') {
                        this.form.asn_name = this.form.asn_name.split('"').join('')
                        this.form.asn_name = this.form.asn_name.split("'").join("")
                    }
                } catch (e) {
                    console.log(e)
                    this.form.asn_name = ''
                }

                this.form.own_path_first = ""
                for ( var i = 0 ; i < this.form.opf_split.length ; i ++ ) {

                    if (this.form.opf_split[i]) {
                        this.form.own_path_first += '1'
                    } else {
                        this.form.own_path_first += '0'
                    }
                }

                this.form.emails_opf = ""
                for ( var i = 0 ; i < this.form.opf_split_e.length ; i ++ ) {
                    if (this.form.opf_split_e[i]) {
                        this.form.emails_opf += '1'
                    } else {
                        this.form.emails_opf += '0'
                    }
                }

                var query = `mutation addASN { updateASN (
                    asn: "`+this.form.asn+`", 
                    H: `+this.form.h+`, 
                    I: `+this.form.i+`, 
                    V: `+this.form.v+`, 
                    peering_asn: "N",
                    asn_class: 0, 
                    organisation: "`+this.organisation + `",
                    p_user: "` + p_user + `",
                    p_as_group: "` + this.form.as_group + `",
                    p_own_path_first: "` + this.form.own_path_first + `",
                    p_emails_opf: "` + this.form.emails_opf + `",
                    p_and_or: "` + this.form.and_or + `",
                    p_asn_name: "` + this.form.asn_name + `",
                    o_and_or: "` + this.selectedASN.and_or + `",
                    o_own_path_first: "` + this.selectedASN.own_path_first + `",
                    o_emails_opf: "` + this.selectedASN.emails_opf + `",
                    o_h: ` + this.selectedASN.h + `,
                    o_i: `+ this.selectedASN.i +`,
                    o_v: `+ this.selectedASN.v + `
                ) {response}}`;

              //  console.log(query);
                const updateASNSettings = await API.graphql(graphqlOperation(query));
               // console.log(updateASNSettings);
                var responseMessage = updateASNSettings.data.updateASN;
                
                // EDIT THIS



                if (responseMessage) {
                    this.msgColor = 'success';
                    this.message = responseMessage[0].response
                } else {
                    this.msgColor = 'red';
                    this.message = "Error editting AS " + this.form.asn;
                }

                if (this.addType == "user") {
                    this.getUserASNs();
                } else {
                    this.getASNs();
                }
                this.shrinkEditWindow();

                this.displayMessage = true;
            }

        },
        addASN: async function () {
            if (this.$refs.form.validate()) {

                var a;
                var ex_peer;
                var emls;

                if (this.addType == "user") {
                    var p_user = this.$store.state.user.username;
                } else {
                    var p_user = "whole_organisation"
                }
             //   console.log(p_user + " : " + this.addType)
                if (this.form.peering == true) {
                    a = "Y"
                } else {
                    a ="N"
                }

                if (this.form.exclude_peer == true) {
                    ex_peer = "Y"
                } else {
                    ex_peer ="N"
                }

                if (this.form.emails == true) {
                    emls = "Y"
                } else {
                    emls ="N"
                }

                if (this.form.asn_name != '') {
                    this.form.asn_name = this.form.asn_name.split('"').join('')
                    this.form.asn_name = this.form.asn_name.split("'").join("")
                }

                this.form.own_path_first = ""
                for ( var i = 0 ; i < this.form.opf_split.length ; i ++ ) {
              //      console.log(i)
              //      console.log(this.form.opf_split[i])
                    if (this.form.opf_split[i]) {
                        this.form.own_path_first += '1'
                    } else {
                        this.form.own_path_first += '0'
                    }
                }

                this.form.emails_opf = ""
                for ( var i = 0 ; i < this.form.opf_split_e.length ; i ++ ) {
              //      console.log(i)
              //      console.log(this.form.opf_split_e[i])
                    if (this.form.opf_split_e[i]) {
                        this.form.emails_opf += '1'
                    } else {
                        this.form.emails_opf += '0'
                    }
                }

                var query = `mutation addASN { updateASN (
                    asn: "`+this.form.asn+`", 
                    H: `+this.form.h+`, 
                    I: `+this.form.i+`, 
                    V: `+this.form.v+`, 
                    peering_asn: "N",
                    asn_class: 0, 
                    organisation: "`+this.organisation + `",
                    p_user: "` + p_user + `",
                    p_as_group: "` + this.form.as_group + `",
                    p_own_path_first: "` + this.form.own_path_first + `",
                    p_emails_opf: "` + this.form.emails_opf + `",
                    p_and_or: "` + this.form.and_or + `",
                    p_asn_name: "` + this.form.asn_name + `",
                    o_and_or: "` + this.selectedASN.and_or + `",
                    o_own_path_first: "` + this.selectedASN.own_path_first + `",
                    o_emails_opf: "` + this.selectedASN.emails_opf + `",
                    o_h: ` + this.selectedASN.h + `,
                    o_i: `+ this.selectedASN.i +`,
                    o_v: `+ this.selectedASN.v + `
                ) {response}}`;
              //  console.log(query)
                const addASN = await API.graphql(graphqlOperation(query));
                var responseMessage = addASN.data.updateASN;
                
                // EDIT THIS
                if (responseMessage) {
                    this.msgColor = 'success';
                    this.message = responseMessage[0].response
                } else {
                    this.msgColor = 'red';
                    this.message = "Error editting AS " + this.form.asn;
                }
                
                if (this.addType == "user") {
                    this.getUserASNs();
                } else {
                    this.getASNs();
                }
                this.shrinkEditWindow();

                this.displayMessage = true;
            }
            

        },
        clearMessage: function () {
            this.displayMessage = false;
            this.message = "";

        },
        prepRemoveASN: async function (asn, type) {
            this.clearMessage();
            this.showEdit = false;
            this.addType = type;
            this.deleteASN = asn;
          //  console.log("prepare remove: " + String(asn) + " : " + type)
            this.showAreYouSure = true;
        },
        removeASN: async function() {
            this.showAreYouSure = false;

            if (this.addType == "user") {
                var p_user = this.$store.state.user.username;
            } else {
                var p_user = "whole_organisation"
            }
         //   console.log(p_user + " : " + this.addType)

            var remQuery = `mutation deleteASN { deleteASN(
                asn: "`+this.deleteASN.asn+`",
                p_alerting_type: "` + this.deleteASN.alerting_type + `",
                p_exclude_peer: "` + this.deleteASN.exclude_peer + `",
                p_H: "` + this.deleteASN.H + `",
                p_I: "` + this.deleteASN.I + `",
                organisation: "` + this.organisation + `",
                p_user: "` + p_user + `",
                p_own_path_first: "` + this.deleteASN.own_path_first + `",
                p_emails_opf: "` + this.deleteASN.emails_opf + `"
            ) {asn } }`;
        //    console.log(remQuery);
            const response = await API.graphql(graphqlOperation(remQuery));
        //    console.log(response);
            var responseMessage = response.data.deleteASN;
            if (!responseMessage) {
                this.msgColor = 'success';
                this.message = "Successfully removed AS " + this.deleteASN.asn;
            } else {
                this.msgColor = 'red';
                this.message = "Error remmoving AS " + this.deleteASN.asn;
            }

            this.getASNs();
            this.getUserASNs();

            this.displayMessage = true;

        },
        shrinkEditWindow: function() {
            this.showEdit = false;
        },
        getAnASNOwner: async function(asn) {
            if (asn == 0) {
                return "None";
            } else {
                try {
                    var qry = `query getASNOwner { getASNOwner ( asn: "` +
                    asn +
                    `" ) {
                            organisation
                        }
                    }`;
                    //console.log(qry);
                    const res = await API.graphql(graphqlOperation(qry));
                    var ownData = res.data.getASNOwner;
                    //console.log(ownData);
                    if (ownData) {
                        if (ownData.length > 0) {
                        //console.log("owner: ");
                        //console.log(ownData[0].organisation);
                            if (ownData[0].organisation) {
                                return ownData[0].organisation;
                            } else {
                                return "Unknown";
                            }
                        } else {
                            return "Unknown";
                        }
                    } else {
                        return "Unknown";
                    }
                } catch (err) {
                    console.log(err);
                    return "Unknown";
                }
            }
        },
        getASNOwners: async function () {
            
            this.is_asn_owners = false
          //  console.log("gettign asn owners")
            var nero = {}
            for ( var i = 0 ; i < this.asn_get_owner.length ; i++ ) {
                var keys = Object.keys(this.asn_own_dict)
                if (!(keys.includes((this.asn_get_owner[i]).toString()))) {
                    nero[this.asn_get_owner[i]] = this.getAnASNOwner(this.asn_get_owner[i])
                }
            }
            for ( var i = 0 ; i < this.asn_get_owner.length ; i++ ) {
                
                
                //console.log("GETTING AN OWNER")
                if (!(keys.includes((this.asn_get_owner[i]).toString()))) {
                    this.asn_own_dict[this.asn_get_owner[i]] = await nero[this.asn_get_owner[i]]
                }
                
                if ( i == this.asn_get_owner.length - 1 ) {
                    //console.log("UPDATETICKER")
                    this.is_asn_owners = false
                    this.$nextTick(() => {
                        // Add the component back in
                        this.is_asn_owners = true;
                    });
                }
            }
        },
        
        getASNs: async function () {
            this.loading = true;
            this.organisation_download_asns = [];

            var asQuery = `query getuserasn { getASNsForOrg ( 
                start_time: ` + Math.floor( Date.now() / 1000 - 172800 ) + `
                ) { 
                    asn 
                    H
                    I 
                    V 
                    notification_count 
                    peering_asn
                    type
                    alerting_type
                    exclude_peer
                    emails
                    and_or
                    asn_name
                    alert_name
                    as_group
                    own_path_first
                    emails_opf
                } 
            }`;

          //  console.log(asQuery);
            const asData = await API.graphql(graphqlOperation(asQuery));
          //  console.log(asData);
            var asns = asData.data.getASNsForOrg;
          //  console.log(asns);

            if (asns.length == 0 || asns === undefined) {
                this.orgASNs = [];
            } else {
                this.orgASNs = [];
                var i;
                this.$store.state.allASNInfoForOrg = [];
                var toPush = [];
                var orgasn = [];
                for (i = 0; i < asns.length; i++) {
                    orgasn = asns[i]
                    if (orgasn.asn_name!='--' && orgasn.alert_name=='--') {
                        try {
                            orgasn.opf_split = orgasn.own_path_first.split("")
                            for ( var j = 0 ; j < orgasn.opf_split.length ; j++ ) {
                                orgasn.opf_split[j] = orgasn.opf_split[j] == 1 ? true : false;
                            }
                        } catch (e) {
                            console.log(e)
                            orgasn.opf_split = [false, false, false, false, false]
                        }

                        try {
                            orgasn.opf_split_e = orgasn.emails_opf.split("")
                            for ( var j = 0 ; j < orgasn.opf_split_e.length ; j++ ) {
                                orgasn.opf_split_e[j] = orgasn.opf_split_e[j] == 1 ? true : false;
                            }
                        } catch (e) {
                            console.log(e)
                            orgasn.opf_split_e = [false, false, false, false, false]
                        }

                        if ( !(this.asn_get_owner.includes(orgasn.asn)) ) {
                            this.asn_get_owner.push(orgasn.asn)
                        }
                        orgasn.human_type = this.asnInvTypes[orgasn.type];
                        toPush.push(
                            {
                                H: orgasn.H,
                                I: orgasn.I,
                                V: orgasn.V,
                                asn: orgasn.asn,
                                count: orgasn.notification_count,
                                peering_asn: orgasn.peering_asn,
                                type: orgasn.human_type,
                                asn_name: orgasn.asn_name
                            }
                        )

                        this.organisation_download_asns.push({
                            'asn': orgasn.asn,
                            'asn_name': orgasn.asn_name,
                            'as_group': orgasn.as_group,
                            'own_assertions': orgasn.opf_split[0] ? 'Y' : 'N',
                            'intra_org_assertions': orgasn.opf_split[4] ? 'Y' : 'N',
                            'own_assertions_emails': orgasn.opf_split_e[0] ? 'Y' : 'N',
                            'path_changes': orgasn.opf_split[1] ? 'Y' : 'N',
                            'path_changes_emails': orgasn.opf_split_e[1] ? 'Y' : 'N',
                            'first_seens': orgasn.opf_split[2] ? 'Y' : 'N',
                            'first_seens_emails': orgasn.opf_split_e[2] ? 'Y' : 'N',
                            'as_in_path_alerts': orgasn.opf_split[3] ? 'Y' : 'N',
                            'as_in_path_alerts_emails': orgasn.opf_split_e[3] ? 'Y' : 'N',
                            'h': orgasn.H,
                            'i': orgasn.I,
                            'v': orgasn.V,
                            'and_or': orgasn.and_or
                        })
               //         console.log(orgasn);
                        this.orgASNs.push(orgasn)
                    }

                    if ( i == asns.length - 1) {
                        this.$store.commit("setOrgASNRules", this.orgASNs)
                    }
                }
                this.$store.state.allASNInfoForOrg = toPush;
                this.getASNOwners()

            }

           this.loading = false;
            
        },
        getUserASNs: async function () {
            this.loading2 = true;
            this.user_download_asns = [];
            var asQuery = `query getuserasn { 
                getASNsForOrg ( 
                    start_time: ` + Math.floor( Date.now() / 1000 - 604800 ) + `,
                    p_user: "` + this.username + `") { 
                    asn 
                    H
                    I 
                    V 
                    notification_count 
                    peering_asn
                    type
                    alerting_type
                    exclude_peer
                    emails
                    and_or
                    asn_name
                    alert_name
                    as_group
                    own_path_first
                    emails_opf
                } 
            }`;

          //  console.log(asQuery);
            const asData = await API.graphql(graphqlOperation(asQuery));
         //   console.log(asData);
            var asns = asData.data.getASNsForOrg;
          //  console.log(asns);

            if (asns.length == 0 || asns === undefined) {
                this.userASNs = [];
            } else {
                this.userASNs = [];
                var userasn=[]
                var i;
                this.$store.state.allASNInfoForUser = [];
                var toPush = [];
                for (i = 0; i < asns.length; i++) {
          //          console.log(i)
           //         console.log(asns[i])
                    userasn=asns[i];
                    if (userasn.asn_name!='--' && userasn.alert_name=='--') {

                        try {
                            userasn.opf_split = userasn.own_path_first.split("")
                            for ( var j = 0 ; j < userasn.opf_split.length ; j++ ) {
                                userasn.opf_split[j] = userasn.opf_split[j] == 1 ? true : false;
                            }
                        } catch (e) {
                            console.log(e)
                            userasn.opf_split = [false, false, false, false, false]
                        }

                        try {
                            userasn.opf_split_e = userasn.emails_opf.split("")
                            for ( var j = 0 ; j < userasn.opf_split_e.length ; j++ ) {
                                userasn.opf_split_e[j] = userasn.opf_split_e[j] == 1 ? true : false;
                            }
                        } catch (e) {
                            console.log(e)
                            userasn.opf_split_e = [false, false, false, false, false]
                        }

                        if ( !(this.asn_get_owner.includes(userasn.asn)) ) {
                            this.asn_get_owner.push(userasn.asn)
                        }

                        userasn.human_type = this.asnInvTypes[userasn.type];
                        toPush.push(
                            {
                                H: userasn.H,
                                I: userasn.I,
                                V: userasn.V,
                                asn: userasn.asn,
                                count: userasn.notification_count,
                                peering_asn: userasn.peering_asn,
                                type: userasn.human_type,
                                asn_name: userasn.asn_name
                            }
                        )
                        this.user_download_asns.push({
                            'asn': userasn.asn,
                            'asn_name': userasn.asn_name,
                            'as_group': userasn.as_group,
                            'own_assertions': userasn.opf_split[0] ? 'Y' : 'N',
                            'intra_org_assertions': userasn.opf_split[4] ? 'Y' : 'N',
                            'own_assertions_emails': userasn.opf_split_e[0] ? 'Y' : 'N',
                            'path_changes': userasn.opf_split[1] ? 'Y' : 'N',
                            'path_changes_emails': userasn.opf_split_e[1] ? 'Y' : 'N',
                            'first_seens': userasn.opf_split[2] ? 'Y' : 'N',
                            'first_seens_emails': userasn.opf_split_e[2] ? 'Y' : 'N',
                            'as_in_path_alerts': userasn.opf_split[3] ? 'Y' : 'N',
                            'as_in_path_alerts_emails': userasn.opf_split_e[3] ? 'Y' : 'N',
                            'h': userasn.H,
                            'i': userasn.I,
                            'v': userasn.V,
                            'and_or': userasn.and_or
                        })
                        this.userASNs.push(userasn);

                    }
                    if ( i == asns.length - 1) {
                        this.$store.commit("setUserASNRules", this.userASNs)
                    }
                }
                this.getASNOwners()
                this.$store.state.allASNInfoForUser = toPush;

            }

           this.loading2 = false;
            
        }
    },
    watch: {
        showAdvanced (n) {
     //       console.log("setting show advanced settings")
     //       console.log(n)
            this.$store.commit("setShowSettings", { 'advancedSettings': n });
     //       console.log(this.$store.state.showSettings)

        }
    }
}
</script>

<style scoped>
    
    table.v-table tbody td:first-child, 
    table.v-table tbody td:not(:first-child), 
    table.v-table tbody th:first-child, 
    table.v-table tbody th:not(:first-child), 
    table.v-table thead td:first-child, 
    table.v-table thead td:not(:first-child), 
    table.v-table thead th:first-child, 
    table.v-table thead th:not(:first-child) {
        padding: 0 5px
    }

    table.v-table tbody td, 
    table.v-table tbody th {
        height: 38px;
    }


    tbody tr:nth-of-type(even) {
      background-color: rgba(0, 0, 0, .05);
    }

    .v-table tr:hover:not(.v-table__expanded__content) {
        background-color: rgba(0, 0, 0, .4);
    }

</style>
